/**
 * CadenceRuleForm
 * Nodes: N#1069 (BRDs 1355–1364) + N#1070 (BRDs 1365–1372) + N#1071 (BRDs 1373–1382)
 *      + N#1072 (BRDs 1383–1391) + N#1073 (BRDs 1392–1404) + N#1074 (BRDs 1405–1411)
 *
 * N#1069 — Identity & Lifecycle (What):
 *  - Category dropdown — master-seeded, no add control (BRD 1)
 *  - Type dropdown — master-seeded, no add control (BRD 2)
 *  - Source Obligation — cascades on category+type, clears on change (BRD 3)
 *  - Obligation Name + Description — read-only mirrors (BRD 4)
 *  - Fulfillment Handler + Template — read-only, auto-derived (BRD 6)
 *  - Start Date — required for activation (BRD 7)
 *  - End Date — optional, validated >= start (BRD 8)
 *
 * N#1070 — Source & Authority (Why):
 *  - Regulatory Source, Reference URL, Policy Ref — read-only mirrors (BRD 1)
 *  - Accountable Role — required for activation, master-seeded (BRD 3)
 *  - Fallback Role — required for activation, master-seeded (BRD 4)
 *  - Accountable ≠ Fallback inline validation (BRD 5)
 *
 * N#1071 — Scope (Who):
 *  - Two modes: Specific (one geography, no expansion) and Region-based
 *    (anchor + optional type filter; descendants materialised) (BRDs 1–3)
 *  - Plain-text filter annotation for audit (BRD 5)
 *  - Materialised list preview; save recomputes it (BRD 4)
 *  - Refresh banner when register changes; owner approves diff (BRDs 6,7,8)
 *  - Activation requires ≥1 geography in materialised list (BRD 10)
 *
 * N#1072 — Applicability (When applicable):
 *  - Three modes: Unconditional (default), Threshold-driven, Questionnaire-driven (BRDs 1, 2)
 *  - Threshold + Questionnaire are read-only mirrors from their libraries (BRDs 3, 4)
 *  - Content predicate (Cadence-authored) with AND/OR combiner (BRD 5)
 *  - Re-evaluation policy is global (Engine Settings), never per-rule (BRD 6)
 *  - Library-archive auto-pauses rules with status
 *    'Paused — Applicability source archived' (BRD 9)
 *
 * N#1073 — Due-Date Trigger (When due):
 *  - Two modes: Calendar-fixed (Short/Long span) and Event-driven (BRDs 1, 2)
 *  - Short span: Daily/Weekly/Fortnightly/Monthly (BRD 3)
 *  - Long span: Bi-monthly/Quarterly/Half-yearly/Annual/Biennial + month-of-period (BRD 4)
 *  - Event-driven: After-event (offset + anchor_from + grace + seed) or
 *    Watch-not-happened (deadline absolute/offset) (BRDs 7–11)
 *  - Auto-derived period_token used in event_key for dedup (BRD 5)
 *  - Library-archive auto-pauses with status 'Paused — Trigger source archived' (BRD 13)
 *
 * N#1074 — Date Exceptions (What if):
 *  - Single exception type: non-working day (weekend OR holiday) (BRD 1)
 *  - Closed-enum action: strict / shift_forward / shift_backward / shift_to_nearest (BRD 2)
 *  - Working-day = in business_days AND not in holiday calendar (BRD 3)
 *  - Shift runs AFTER period_token derivation — dedup uses original period (BRD 4)
 *  - Grace + reminder lead anchor to the SHIFTED date (BRD 5)
 *  - Weekend+holiday counts once (BRD 6)
 *  - Dry-run preview shows original, final, reason, shifted badge (BRD 7)
 *
 * N#1075 — Parameters (Reference + Tolerances):
 *  - Two modes per rule: use_defaults | modify (mutually exclusive) (BRDs 1, 2, 3)
 *  - Constructive switch use_defaults → modify seeds from globals (BRD 4)
 *  - Destructive switch modify → use_defaults requires explicit confirmation (BRD 5)
 *  - Six parameter values across two sub-groups (BRD 6)
 *  - Catch-up window effective = min(rule, global); hint when capped (BRD 7)
 *  - Global changes propagate live to use_defaults rules + audited (BRD 8)
 */
import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { ChevronDown, Check, Trash2, ArrowUp, ArrowDown, ArrowLeft, ArrowRight, X, Plus } from 'lucide-react';
import extractApiError from '../../utils/apiError';
import {
  SectionStatus, isFirstStage, isLastStage, clampStage, isBubbleLocked,
} from '../../utils/cadenceWizardNav';
import CadenceStepper, { StepItem } from './CadenceStepper';
import FormControl from '../common/FormControl';
import Button from '../common/Button';
import Toast, { ToastMessage } from '../common/Toast';
import { confirmWarning, confirmActive } from '../common/confirm';
import { entityRegistryService, EntityType, EntityRecord } from '../../services/entityRegistryService';
import {
  CadenceCategory, CadenceRole,
  SourceObligationItem, CadenceRule,
  CadenceRuleCreate, CadenceRuleUpdate,
  getCategories, getRoles,
  getSourceObligations, getSourceTemplates, createRule, updateRule, activateRule,
  // N#1071
  ScopeMode, ScopeConfig, ScopeDiff, OrgLocation, GeographyType,
  getScopeLocations, getGeographyTypes,
  getRuleScope, saveRuleScope, getScopeDiff, approveScopeRefresh, previewScope, ScopeLocation,
  // N#1072
  ApplicabilityMode, ApplicabilityConfig, ApplicabilityPayload,
  ThresholdLibraryItem,
  PredicateConditionInput, PredicateOperator,
  getThresholdLibrary,
  Checklist, ChecklistItem, getChecklists, getChecklistItems,
  getRuleApplicability, saveRuleApplicability,
  // N#1073
  TriggerMode, TriggerSpan, CalendarFrequency, MonthlySpecifier, DayOfWeek,
  TriggerVariant, OffsetUnit, AnchorFrom, FirstFireMode, DeadlineKind, DeadlineReference,
  EventLibraryItem, TriggerConfig, TriggerSavePayload,
  getEventLibrary, getRuleTrigger, saveRuleTrigger,
  // N#1074
  DateExceptionAction, HolidayCalendar, DateExceptionConfig, DateExceptionPayload, DryRunRow,
  getHolidayCalendars, getRuleDateExceptions, saveRuleDateExceptions, getRuleDryRun,
  // N#1075
  ParametersMode, GlobalParameters, RuleParametersConfig, RuleParametersSavePayload,
  DiscardPreview,
  getGlobalParameters, getRuleParameters, saveRuleParameters,
  getRuleParametersDiscardPreview,
  // N#1080
  RuleVersionHistory,
  listRuleVersions, rolloutRuleVersion,
} from '../../services/cadenceService';
import { useFieldValidation } from '../../validation/useFieldValidation';
import { ValidatedDatePicker } from '../../validation/ValidatedFields';

interface Props {
  rule?: CadenceRule;
  onSaved: (rule: CadenceRule) => void;
  onCancel: () => void;
  /** Edit-only: invoked when the user clicks the destructive Delete button at the footer. */
  onDelete?: () => void;
  /** Called with footer JSX so the parent can render it in Modal.Footer */
  renderFooter?: (footer: React.ReactNode) => void;
  /**
   * Migration 114 — when opened from a template's Cadence tab, the source
   * binding is pre-filled and locked. The picker section renders as a
   * read-only chip; on submit, source_type_id + source_record_id are sent
   * straight from this prop.
   */
  lockedSource?: {
    source_type_id: number;
    source_record_id: number;
    source_type_code: string;
    name: string;
    display_name: string;
  };
  /** In-context module create: restrict the "Linked to" type picker to these
   *  registry type_codes (the module's allowed cadence kinds). The record is
   *  still freely chosen. Omitted = all cadence-eligible types. */
  allowedTypeCodes?: string[];
  /** RBAC (extent-only): when false, all mutation buttons (Save / Activate /
   *  Pause / Resume / Delete) are hidden — the form is view-only. Defaults to
   *  true for callers that don't gate. */
  canModify?: boolean;
}

interface FormState {
  category_id: number | '';
  // Migration 114 — canonical polymorphic source binding.
  source_type_id: number | '';
  source_record_id: number | '';
  // Legacy form fields kept so the in-form picker UI (when not locked) can
  // still mutate them; the submit handler maps the legacy picker selection
  // into the new pair at the boundary.
  source_obligation_id: number | '';
  source_module: string;
  source_type: string;
  accountable_role_id: number | '';
  fallback_role_id: number | '';
  start_date: string;
  end_date: string;
}

// Category dropdown removed — Linked-to picker is the single source of
// binding. SCH_cadence_rules.category_id is still NOT NULL, but the id is
// resolved SERVER-side from the bound source kind. Shipping a hardcoded id
// from here broke rule creation on installs whose SCH_cadence_categories rows
// carry different ids (on-prem: "Invalid or inactive category.").
const EMPTY: FormState = {
  category_id: '',
  source_type_id: '', source_record_id: '',
  source_obligation_id: '',
  source_module: '', source_type: '',
  accountable_role_id: '', fallback_role_id: '',
  start_date: '', end_date: '',
};

// -- Two-column wizard layout ------------------------------------------------
// Left: vertical nav list of all sections with number badge + status chip.
// Right: the active section's form fields.
// activeSection is lifted to the parent form component.

// Content panel rendered on the right — always visible when active
const RuleSection: React.FC<{
  number: number;
  title: string;
  status: SectionStatus;
  hint?: string;
  children: React.ReactNode;
}> = ({ title, status, hint, children }) => (
  <div className={`rule-panel is-${status}`}>
    <div className="rule-panel-head">
      <h3 className="rule-panel-title">{title}</h3>
      <span className={`rule-section-chip s-${status}`}>
        {status === 'saved' ? 'Saved' : status === 'partial' ? 'Partial' : 'Not saved'}
      </span>
    </div>
    {hint && <p className="rule-section-hint">{hint}</p>}
    <div className="rule-panel-body form-fields">
      {children}
    </div>
  </div>
);

const CadenceRuleForm: React.FC<Props> = ({ rule: initialRule, onSaved, onCancel, onDelete, renderFooter, lockedSource, allowedTypeCodes, canModify = true }) => {
  // After a fresh Create, the form switches into edit mode *locally* instead
  // of closing — so the operator can fill the remaining sections without
  // re-opening the rule. `activeRule` reflects the current persisted state,
  // and the rest of the component reads from `rule` (= activeRule) so
  // existing code paths keep working.
  const [activeRule, setActiveRule] = useState<CadenceRule | undefined>(initialRule);
  const rule = activeRule;
  const isEdit = !!rule;

  const [categories, setCategories] = useState<CadenceCategory[]>([]);
  const [roles, setRoles] = useState<CadenceRole[]>([]);
  const [sources, setSources] = useState<SourceObligationItem[]>([]);
  // Linked-to two-step picker state (replaces the legacy source-obligation flow).
  const [linkTypes, setLinkTypes] = useState<EntityType[]>([]);
  const [linkRecords, setLinkRecords] = useState<EntityRecord[]>([]);
  const [linkRecordsLoading, setLinkRecordsLoading] = useState(false);
  const [form, setForm] = useState<FormState>(EMPTY);
  const [selectedSource, setSelectedSource] = useState<SourceObligationItem | null>(null);
  const [toast, setToast] = useState<ToastMessage | null>(null);
  const [activating, setActivating] = useState(false);
  const [activeSection, setActiveSection] = useState(1);
  const vh = useFieldValidation();
  const { errors, validateField, clearError, validateAll, setError } = vh;

  // ---- N#1071 Scope (Who) state ----
  const [allLocations, setAllLocations] = useState<OrgLocation[]>([]);
  // N#1071 — geography-type master (ORG_geography_types) drives the type filter.
  const [geographyTypes, setGeographyTypes] = useState<GeographyType[]>([]);
  const [scopeMode, setScopeMode] = useState<ScopeMode>('specific');
  // Specific mode now holds a list (N cherry-picked geos); post-migration 065.
  const [specificLocationIds, setSpecificLocationIds] = useState<number[]>([]);
  const [regionAnchorId, setRegionAnchorId] = useState<number | ''>('');  // '' = Global
  const [geographyTypeId, setGeographyTypeId] = useState<number | ''>('');  // '' = no type filter
  const [scope, setScope] = useState<ScopeConfig | null>(null);
  const [scopeDiff, setScopeDiff] = useState<ScopeDiff | null>(null);
  const [refreshOpen, setRefreshOpen] = useState(false);
  // N#1071 — live preview of qualifying geographies (before save).
  const [scopePreview, setScopePreview] = useState<ScopeLocation[] | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  // ---- N#1072 Applicability (When) state ----
  const [thresholds, setThresholds] = useState<ThresholdLibraryItem[]>([]);
  // N#1072 — checklist-driven applicability sources audit GRC-Common Checklists.
  const [checklists, setChecklists] = useState<Checklist[]>([]);
  const [appMode, setAppMode] = useState<ApplicabilityMode>('unconditional');
  const [appThresholdId, setAppThresholdId] = useState<number | ''>('');
  const [appQuestionnaireId, setAppQuestionnaireId] = useState<number | ''>('');
  const [appCombiner, setAppCombiner] = useState<'AND' | 'OR'>('AND');
  const [appConditions, setAppConditions] = useState<PredicateConditionInput[]>([]);
  const [appConfig, setAppConfig] = useState<ApplicabilityConfig | null>(null);
  // Checklist-driven mode: the yes/no items of the currently-picked checklist.
  const [checklistItems, setChecklistItems] = useState<ChecklistItem[]>([]);

  // ---- N#1073 Trigger (When due) state ----
  const [events, setEvents] = useState<EventLibraryItem[]>([]);
  const [trigger, setTrigger] = useState<TriggerConfig | null>(null);
  const [trigMode, setTrigMode] = useState<TriggerMode>('calendar');
  // Calendar
  const [trigSpan, setTrigSpan] = useState<TriggerSpan>('short');
  const [trigFrequency, setTrigFrequency] = useState<CalendarFrequency | ''>('');
  const [trigDayOfWeek, setTrigDayOfWeek] = useState<DayOfWeek | ''>('');
  const [trigMonthlySpec, setTrigMonthlySpec] = useState<MonthlySpecifier | ''>('');
  const [trigNthN, setTrigNthN] = useState<number | ''>('');
  const [trigNthDay, setTrigNthDay] = useState<DayOfWeek | ''>('');
  const [trigSpecifiedDay, setTrigSpecifiedDay] = useState<number | ''>('');
  const [trigMonthOfPeriod, setTrigMonthOfPeriod] = useState<number | ''>('');
  // Event
  const [trigEventId, setTrigEventId] = useState<number | ''>('');
  const [trigVariant, setTrigVariant] = useState<TriggerVariant>('after_event');
  const [trigOffsetValue, setTrigOffsetValue] = useState<number | ''>('');
  const [trigOffsetUnit, setTrigOffsetUnit] = useState<OffsetUnit>('days');
  const [trigAnchorFrom, setTrigAnchorFrom] = useState<AnchorFrom>('completion_date');
  const [trigGrace, setTrigGrace] = useState<number>(0);
  const [trigFirstFire, setTrigFirstFire] = useState<FirstFireMode>('wait_first_anchor');
  const [trigSeedDate, setTrigSeedDate] = useState<string>('');
  const [trigDeadlineKind, setTrigDeadlineKind] = useState<DeadlineKind>('offset');
  const [trigDeadlineDate, setTrigDeadlineDate] = useState<string>('');
  const [trigDeadlineOffset, setTrigDeadlineOffset] = useState<number | ''>('');
  const [trigDeadlineRef, setTrigDeadlineRef] = useState<DeadlineReference>('period_start');

  // ---- N#1074 Date Exceptions (What if) state ----
  const [calendars, setCalendars] = useState<HolidayCalendar[]>([]);
  const [dxAction, setDxAction] = useState<DateExceptionAction>('shift_forward');
  const [dxBusinessDays, setDxBusinessDays] = useState<number[]>([1, 2, 3, 4, 5]);
  const [dxCalendarId, setDxCalendarId] = useState<number | ''>('');
  const [dxConfig, setDxConfig] = useState<DateExceptionConfig | null>(null);
  const [dryRun, setDryRun] = useState<DryRunRow[]>([]);
  const [dryRunLoading, setDryRunLoading] = useState(false);

  // ---- N#1075 Parameters state ----
  const [globalParams, setGlobalParams] = useState<GlobalParameters | null>(null);
  const [paramConfig, setParamConfig] = useState<RuleParametersConfig | null>(null);
  const [paramMode, setParamMode] = useState<ParametersMode>('use_defaults');
  const [pHolidayCalId, setPHolidayCalId] = useState<number | ''>('');
  const [pBusinessDays, setPBusinessDays] = useState<number[]>([1, 2, 3, 4, 5]);
  const [pTimeZone, setPTimeZone] = useState<string>('Asia/Kolkata');
  const [pReminderLead, setPReminderLead] = useState<number>(7);
  const [pGracePeriod, setPGracePeriod] = useState<number>(5);
  const [pCatchUpWindow, setPCatchUpWindow] = useState<number>(7);
  // True while a stage save triggered by Prev/Next/bubble is in flight —
  // disables the whole footer + stepper to prevent double-submits / races.
  const [navigating, setNavigating] = useState(false);
  // Stepper colouring is purely positional (see the stepper mapping below):
  // steps before the active one are "done" (green), the active one is filled
  // lavender, later ones are grey. activeSection only advances after a
  // successful saveStage, so "before active" == "successfully passed".
  const [discardOpen, setDiscardOpen] = useState(false);
  const [discardPreview, setDiscardPreview] = useState<DiscardPreview | null>(null);

  // ---- N#1080 Versions state ----
  const [versions, setVersions] = useState<RuleVersionHistory | null>(null);
  const [versionsLoading, setVersionsLoading] = useState(false);
  const [rolloutNotes, setRolloutNotes] = useState<string>('');
  const [rolloutBusy, setRolloutBusy] = useState(false);

  const categoryName = categories.find(c => c.id === form.category_id)?.name ?? '';

  // Load master lists
  useEffect(() => {
    getCategories().then(setCategories).catch(() => { });
    getRoles().then(setRoles).catch(() => { });  // N#1070 BRDs 3 & 4
    // N#1071 — load geography register + distinct type values
    getScopeLocations().then(setAllLocations).catch(() => setAllLocations([]));
    getGeographyTypes().then(setGeographyTypes).catch(() => setGeographyTypes([]));
    // N#1072 — load applicability libraries
    getThresholdLibrary().then(setThresholds).catch(() => setThresholds([]));
    getChecklists().then(setChecklists).catch(() => setChecklists([]));
    // N#1073 — load event library
    getEventLibrary().then(setEvents).catch(() => setEvents([]));
    // N#1074 — load holiday calendars
    getHolidayCalendars().then(setCalendars).catch(() => setCalendars([]));
    // N#1075 — load global parameters
    getGlobalParameters().then(setGlobalParams).catch(() => setGlobalParams(null));
  }, []);

  // N#1075 — seed form when editing
  const loadParameters = useCallback(async (ruleId: number) => {
    try {
      const cfg = await getRuleParameters(ruleId);
      setParamConfig(cfg);
      setParamMode(cfg.mode);
      // Seed editable fields from effective values
      setPHolidayCalId(cfg.holiday_calendar_id ?? '');
      setPBusinessDays(cfg.business_day_definition || [1, 2, 3, 4, 5]);
      setPTimeZone(cfg.time_zone || 'Asia/Kolkata');
      setPReminderLead(cfg.reminder_lead_days ?? 7);
      setPGracePeriod(cfg.grace_period_days ?? 5);
      setPCatchUpWindow(cfg.catch_up_window_days ?? 7);
    } catch {
      setParamConfig(null);
    }
  }, []);
  useEffect(() => {
    if (rule?.id) loadParameters(rule.id);
  }, [rule, loadParameters]);

  // N#1080 — versions loader
  const loadVersions = useCallback(async (ruleId: number) => {
    setVersionsLoading(true);
    try {
      setVersions(await listRuleVersions(ruleId));
    } catch (err: any) {
      setToast({ type: 'error', message: extractApiError(err, 'Failed to load versions.') });
    } finally {
      setVersionsLoading(false);
    }
  }, []);
  useEffect(() => { if (rule?.id) loadVersions(rule.id); }, [rule, loadVersions]);

  const handleRollout = async () => {
    if (!rule) return;
    const nextV = (versions?.current_version_number ?? 1) + 1;
    const ok = await confirmWarning({
      title: `Roll out v${nextV}?`,
      text: `Snapshots the current rule + scope into v${nextV - 1} history and starts v${nextV}.`,
      confirmText: `Roll out v${nextV}`,
    });
    if (!ok) return;
    if (rolloutNotes.trim()) {
      const valid = validateAll([{ name: 'rollout_notes', code: 110, value: rolloutNotes }]);
      if (!valid) return;
    }
    setRolloutBusy(true);
    try {
      const fresh = await rolloutRuleVersion(rule.id, rolloutNotes || undefined);
      setVersions(fresh);
      setRolloutNotes('');
    } catch (err: any) {
      setToast({ type: 'error', message: extractApiError(err, 'Rollout failed.') });
    } finally {
      setRolloutBusy(false);
    }
  };

  // N#1074 — load existing date-exception config when editing
  const loadDateExceptions = useCallback(async (ruleId: number) => {
    try {
      const cfg = await getRuleDateExceptions(ruleId);
      setDxConfig(cfg);
      setDxAction(cfg.action);
      setDxBusinessDays(cfg.business_days || [1, 2, 3, 4, 5]);
      setDxCalendarId(cfg.holiday_calendar_id ?? '');
    } catch {
      setDxConfig(null);
    }
  }, []);
  const loadDryRun = useCallback(async (ruleId: number) => {
    setDryRunLoading(true);
    try {
      const res = await getRuleDryRun(ruleId, 6);
      setDryRun(res.rows || []);
    } catch {
      setDryRun([]);
    } finally {
      setDryRunLoading(false);
    }
  }, []);
  useEffect(() => {
    if (rule?.id) {
      loadDateExceptions(rule.id);
      loadDryRun(rule.id);
    }
  }, [rule, loadDateExceptions, loadDryRun]);

  // N#1073 — load existing trigger when editing
  const loadTrigger = useCallback(async (ruleId: number) => {
    try {
      const t = await getRuleTrigger(ruleId);
      setTrigger(t);
      if (t) {
        setTrigMode(t.mode);
        // Calendar
        setTrigSpan((t.span as TriggerSpan) || 'short');
        setTrigFrequency((t.frequency as CalendarFrequency) || '');
        setTrigDayOfWeek((t.day_of_week as DayOfWeek) || '');
        setTrigMonthlySpec((t.monthly_specifier as MonthlySpecifier) || '');
        setTrigNthN(t.nth_weekday_n ?? '');
        setTrigNthDay((t.nth_weekday_day as DayOfWeek) || '');
        setTrigSpecifiedDay(t.specified_day ?? '');
        setTrigMonthOfPeriod(t.month_of_period ?? '');
        // Event
        setTrigEventId(t.event_id ?? '');
        setTrigVariant((t.variant as TriggerVariant) || 'after_event');
        setTrigOffsetValue(t.offset_value ?? '');
        setTrigOffsetUnit((t.offset_unit as OffsetUnit) || 'days');
        setTrigAnchorFrom((t.anchor_from as AnchorFrom) || 'completion_date');
        setTrigGrace(t.watch_grace_days ?? 0);
        setTrigFirstFire((t.first_fire_mode as FirstFireMode) || 'wait_first_anchor');
        setTrigSeedDate(t.seed_date ?? '');
        setTrigDeadlineKind((t.deadline_kind as DeadlineKind) || 'offset');
        setTrigDeadlineDate(t.deadline_date ?? '');
        setTrigDeadlineOffset(t.deadline_offset_days ?? '');
        setTrigDeadlineRef((t.deadline_reference as DeadlineReference) || 'period_start');
      }
    } catch {
      setTrigger(null);
    }
  }, []);

  useEffect(() => {
    if (rule?.id) loadTrigger(rule.id);
  }, [rule, loadTrigger]);

  // N#1072 — load existing applicability when editing
  const loadApplicability = useCallback(async (ruleId: number) => {
    try {
      const cfg = await getRuleApplicability(ruleId);
      setAppConfig(cfg);
      setAppMode(cfg.mode);
      setAppThresholdId(cfg.threshold_id ?? '');
      setAppQuestionnaireId(cfg.questionnaire_id ?? '');
      setAppCombiner(cfg.predicate_combiner || 'AND');
      setAppConditions(cfg.conditions.map(c => ({
        question_id: c.question_id,
        operator: c.operator,
        expected_value: c.expected_value,
        display_order: c.display_order,
      })));
      setChecklistItems((cfg.questionnaire as any)?.items ?? []);
    } catch {
      setAppConfig(null);
    }
  }, []);

  useEffect(() => {
    if (rule?.id) loadApplicability(rule.id);
  }, [rule, loadApplicability]);

  // When the user picks a questionnaire from the dropdown, fetch its question list.
  // appConfig is intentionally excluded — we only want to re-run when the user
  // changes the mode or questionnaire selection, not whenever loadApplicability
  // updates appConfig (which would create an infinite loop).
  const appConfigRef = React.useRef(appConfig);
  appConfigRef.current = appConfig;
  useEffect(() => {
    if (appMode !== 'questionnaire' || !appQuestionnaireId) {
      setChecklistItems((appConfigRef.current?.questionnaire as any)?.items ?? []);
      return;
    }
    getChecklistItems(appQuestionnaireId as number)
      .then(items => setChecklistItems(items || []))
      .catch(() => setChecklistItems([]));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appMode, appQuestionnaireId]);

  // N#1071 — load existing scope when editing
  const loadScope = useCallback(async (ruleId: number) => {
    try {
      const s = await getRuleScope(ruleId);
      setScope(s);
      if (s) {
        setScopeMode(s.scope_mode);
        // Prefer the new multi-pick list; fall back to legacy single FK for
        // pre-migration rows that haven't been re-saved yet.
        const ids = (s.specific_location_ids && s.specific_location_ids.length)
          ? s.specific_location_ids
          : (s.specific_location_id ? [s.specific_location_id] : []);
        setSpecificLocationIds(ids);
        setRegionAnchorId(s.region_anchor_id ?? '');
        setGeographyTypeId(s.geography_type_id ?? '');
        if (s.needs_refresh === 1) {
          try {
            const d = await getScopeDiff(ruleId);
            setScopeDiff(d);
          } catch { /* ignore */ }
        }
      }
    } catch {
      setScope(null);
    }
  }, []);

  useEffect(() => {
    if (rule?.id) loadScope(rule.id);
  }, [rule, loadScope]);

  // Seed form when editing
  useEffect(() => {
    if (rule) {
      setForm({
        category_id: rule.category_id,
        source_type_id: rule.source_type_id ?? '',
        source_record_id: rule.source_record_id ?? '',
        source_obligation_id: rule.source_obligation_id ?? '',
        source_module: rule.source_module ?? '',
        source_type: rule.source_type ?? '',
        accountable_role_id: rule.accountable_role_id ?? '',
        fallback_role_id: rule.fallback_role_id ?? '',
        start_date: rule.start_date ?? '',
        end_date: rule.end_date ?? '',
      });
    }
  }, [rule]);

  // Migration 114 — when opened from a template's Cadence tab, prefill the
  // polymorphic source binding and leave the legacy picker idle.
  useEffect(() => {
    if (!lockedSource || rule) return;
    setForm(f => ({
      ...f,
      source_type_id: lockedSource.source_type_id,
      source_record_id: lockedSource.source_record_id,
      source_obligation_id: lockedSource.source_record_id,
      source_module: '',
      source_type: lockedSource.source_type_code.toLowerCase(),
    }));
  }, [lockedSource, rule]);

  // Linked-to: load only cadence-eligible registry kinds (required_for_cadence).
  useEffect(() => {
    entityRegistryService.listTypes(true, true).then(setLinkTypes).catch(() => setLinkTypes([]));
  }, []);

  // In-context single-type module: pre-select the only allowed type on create.
  useEffect(() => {
    if (isEdit || lockedSource) return;
    if (!allowedTypeCodes || allowedTypeCodes.length !== 1) return;
    const t = linkTypes.find(x => x.type_code === allowedTypeCodes[0]);
    if (t) setForm(f => (f.source_type_id === '' ? { ...f, source_type_id: t.id, source_type: t.type_code.toLowerCase() } : f));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [linkTypes, allowedTypeCodes, isEdit, lockedSource]);

  // Linked-to: load records when type changes.
  //
  // Feeds off /sch/source-templates, NOT /utl/lookup. The generic lookup adds an
  // approval predicate derived from whatever approval-ish column the backing
  // table happens to have — and for registers with no maker-checker workflow
  // (com_filings has an `is_approved` column that nothing ever sets to 1) that
  // predicate hides 100% of the rows, so the picker read "No items found" while
  // the records plainly existed. source-templates applies exactly the filters
  // the backend enforces on save (_validate_source_binding: caller's entity,
  // not soft-deleted, discriminator-aware), so what the picker offers and what
  // create/update accepts can no longer disagree.
  useEffect(() => {
    const tid = form.source_type_id;
    if (!tid) { setLinkRecords([]); return; }
    const t = linkTypes.find(x => x.id === tid);
    if (!t) return;
    let alive = true;
    setLinkRecordsLoading(true);
    getSourceTemplates(t.type_code)
      .then(items => {
        if (!alive) return;
        setLinkRecords(items.map(i => ({
          id: i.source_record_id,
          label: i.name,
          code: i.code ?? null,
        })));
      })
      .catch(() => { if (alive) setLinkRecords([]); })
      .finally(() => { if (alive) setLinkRecordsLoading(false); });
    return () => { alive = false; };
  }, [form.source_type_id, linkTypes]);

  // Record picker options. A locked source (the form opened from a template's
  // own Cadence tab) may sit outside the fetched page, so inject it explicitly
  // and its label always renders.
  const linkRecordOptions = useMemo(() => {
    const opts = linkRecords.map(r => ({
      value: r.id as number,
      label: r.code ? `${r.code} — ${r.label}` : r.label,
    }));
    if (
      lockedSource &&
      lockedSource.source_record_id != null &&
      !opts.some(o => o.value === lockedSource.source_record_id)
    ) {
      opts.unshift({ value: lockedSource.source_record_id, label: lockedSource.name });
    }
    return opts;
  }, [linkRecords, lockedSource]);

  // Cascade: load source obligations when category+type changes (N#1069 BRD 3)
  const loadSources = useCallback(async (catName: string) => {
    if (!catName) { setSources([]); return; }
    try { setSources(await getSourceObligations(catName)); }
    catch { setSources([]); }
  }, []);

  useEffect(() => { loadSources(categoryName); }, [categoryName, loadSources]);

  // Restore selectedSource when editing
  useEffect(() => {
    if (rule?.source_obligation_id && sources.length) {
      const found = sources.find(s => s.source_id === rule.source_obligation_id) ?? null;
      if (found) {
        setSelectedSource(found);
      } else if (rule.obligation_name) {
        setSelectedSource({
          source_module: rule.source_module ?? '',
          source_type: rule.source_type ?? '',
          source_id: rule.source_obligation_id ?? 0,
          code: '',
          name: rule.obligation_name ?? '',
          description: rule.obligation_description ?? '',
          template_reference: rule.template_reference ?? '',
          regulatory_source: rule.regulatory_source ?? undefined,
          reference_url: rule.reference_url ?? undefined,
          policy_ref: rule.policy_ref ?? undefined,
        });
      }
    }
  }, [rule, sources]);

  // ---- Handlers ----

  const handleSourceChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const srcId = e.target.value ? parseInt(e.target.value) : '';
    const found = sources.find(s => s.source_id === srcId) ?? null;
    setSelectedSource(found);
    setForm(f => ({
      ...f,
      source_obligation_id: srcId,
      source_module: found?.source_module ?? '',
      source_type: found?.source_type ?? '',
    }));
  };

  // N#1070 BRD 5 T#3112 — inline validation: fallback must differ
  const roleConflict =
    form.accountable_role_id !== '' &&
    form.fallback_role_id !== '' &&
    form.accountable_role_id === form.fallback_role_id;

  // Stable ref for footer callbacks — updated every render so the effect closure
  // never goes stale, without adding these to the dep array (which would loop).
  const footerCbRef = React.useRef({
    onDelete: onDelete as (() => void) | undefined,
    renderFooter: renderFooter as ((node: React.ReactNode) => void) | undefined,
    handleActivate: () => {},
    onPrev: () => {},
    onNext: () => {},
    onFinish: () => {},
  });
  footerCbRef.current.onDelete = onDelete;
  footerCbRef.current.renderFooter = renderFooter;

  const validate = (): string => {
    if (!form.source_obligation_id) return 'Source obligation is required.';
    if (!form.accountable_role_id) return 'Accountable authority is required.';
    if (roleConflict) return 'Fallback authority must differ from accountable authority.';
    if (form.start_date && form.end_date && form.end_date < form.start_date) {
      setError('end_date', 'End date must be on or after start date.');
      return 'End date must be on or after start date.';
    }
    return '';
  };

  // Build optional sub-section payloads to persist alongside the rule on first save.
  // Returns null when the user hasn't configured the section.
  const buildScopePayload = () => {
    if (scopeMode === 'specific') {
      if (!specificLocationIds.length) return null;
      return {
        scope_mode: 'specific' as ScopeMode,
        specific_location_ids: specificLocationIds,
        region_anchor_id: null,
        geography_type_id: null,
      };
    }
    // region_based — Global (no anchor) or anchor + optional type filter
    if (regionAnchorId === '' && geographyTypeId === '') return null;
    return {
      scope_mode: 'region_based' as ScopeMode,
      specific_location_ids: [],
      region_anchor_id: regionAnchorId === '' ? null : (regionAnchorId as number),
      geography_type_id: geographyTypeId === '' ? null : (geographyTypeId as number),
    };
  };

  // N#1071 — live preview of qualifying geographies as the filter changes, so the
  // user sees the materialised set BEFORE saving. Uses the same resolver as save.
  useEffect(() => {
    if (activeSection !== 3) return;
    const payload = buildScopePayload();
    if (!payload) { setScopePreview(null); return; }
    let cancelled = false;
    setPreviewLoading(true);
    previewScope(payload)
      .then(rows => { if (!cancelled) setScopePreview(rows); })
      .catch(() => { if (!cancelled) setScopePreview(null); })
      .finally(() => { if (!cancelled) setPreviewLoading(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSection, scopeMode, regionAnchorId, geographyTypeId, specificLocationIds]);

  const buildApplicabilityPayload = (): ApplicabilityPayload | null => {
    if (appMode === 'unconditional') {
      // Only persist explicitly when editing (overrides any earlier mode);
      // skip on create to leave the rule with its default unconditional state.
      return isEdit ? { mode: 'unconditional' } : null;
    }
    if (appMode === 'threshold') {
      if (!appThresholdId) return null;
      return { mode: 'threshold', threshold_id: appThresholdId as number };
    }
    // questionnaire
    if (!appQuestionnaireId || appConditions.length < 1) return null;
    return {
      mode: 'questionnaire',
      questionnaire_id: appQuestionnaireId as number,
      predicate_combiner: appCombiner,
      conditions: appConditions,
    };
  };

  const buildParametersPayload = (): RuleParametersSavePayload | null => {
    // On create, only persist when user explicitly chose 'modify' — defaults need no row.
    if (!isEdit && paramMode === 'use_defaults') return null;
    if (paramMode === 'use_defaults') {
      return { mode: 'use_defaults', confirm_discard: true };
    }
    return {
      mode: 'modify',
      holiday_calendar_id: pHolidayCalId === '' ? null : (pHolidayCalId as number),
      business_day_definition: pBusinessDays,
      time_zone: pTimeZone,
      reminder_lead_days: pReminderLead,
      grace_period_days: pGracePeriod,
      catch_up_window_days: pCatchUpWindow,
    };
  };

  const buildDateExceptionPayload = (): DateExceptionPayload | null => {
    // Skip when user hasn't changed any defaults and not in edit mode.
    const isDefaultAction = dxAction === 'shift_forward';
    const isDefaultDays = JSON.stringify(dxBusinessDays) === '[1,2,3,4,5]';
    const noCalendar = dxCalendarId === '' || dxCalendarId === null;
    if (!isEdit && isDefaultAction && isDefaultDays && noCalendar) return null;
    return {
      action: dxAction,
      business_days: dxBusinessDays,
      holiday_calendar_id: dxCalendarId === '' ? null : (dxCalendarId as number),
    };
  };

  const buildTriggerPayload = (): TriggerSavePayload | null => {
    if (trigMode === 'calendar') {
      if (!trigFrequency) return null;
      return {
        mode: 'calendar',
        span: trigSpan,
        frequency: trigFrequency as CalendarFrequency,
        day_of_week: trigDayOfWeek || null,
        monthly_specifier: trigMonthlySpec || null,
        nth_weekday_n: trigNthN === '' ? null : (trigNthN as number),
        nth_weekday_day: trigNthDay || null,
        specified_day: trigSpecifiedDay === '' ? null : (trigSpecifiedDay as number),
        month_of_period: trigMonthOfPeriod === '' ? null : (trigMonthOfPeriod as number),
      };
    }
    // event
    if (!trigEventId) return null;
    return {
      mode: 'event',
      event_id: trigEventId as number,
      variant: trigVariant,
      offset_value: trigVariant === 'after_event' && trigOffsetValue !== ''
        ? (trigOffsetValue as number) : null,
      offset_unit: trigVariant === 'after_event' ? trigOffsetUnit : null,
      anchor_from: trigVariant === 'after_event' ? trigAnchorFrom : null,
      watch_grace_days: trigVariant === 'after_event' ? trigGrace : 0,
      first_fire_mode: trigVariant === 'after_event' ? trigFirstFire : null,
      seed_date: trigVariant === 'after_event' && trigFirstFire === 'seed_date' ? trigSeedDate : null,
      deadline_kind: trigVariant === 'watch_not_happened' ? trigDeadlineKind : null,
      deadline_date: trigVariant === 'watch_not_happened' && trigDeadlineKind === 'absolute' ? trigDeadlineDate : null,
      deadline_offset_days: trigVariant === 'watch_not_happened' && trigDeadlineKind === 'offset' && trigDeadlineOffset !== ''
        ? (trigDeadlineOffset as number) : null,
      deadline_reference: trigVariant === 'watch_not_happened' && trigDeadlineKind === 'offset' ? trigDeadlineRef : null,
    };
  };

  // Master save: persists the rule header AND every sub-section the user has
  // configured (scope, applicability, trigger, date-exceptions, parameters).
  // One click in both Add and Edit modes. On Create, transitions Add→Edit
  // locally so subsequent saves go to the same rule_id without re-opening.
  // Persist the rule header (stages 1 Identity + 2 Authority). Returns the saved
  // rule on success, or null when validation fails / the API errors (toast shown).
  const saveHeader = async (): Promise<CadenceRule | null> => {
    // N#1070 — accountable is mandatory, but Identity (section 1) saves before the
    // Authority step (section 2). Only enforce once the user is on/past Authority,
    // so leaving Identity doesn't trip a field that lives on the next step.
    if (activeSection >= 2 && !form.accountable_role_id) {
      setToast({ type: 'error', message: 'Accountable authority is required.' }); return null;
    }
    if (roleConflict) { setToast({ type: 'error', message: 'Fallback authority must differ from accountable authority.' }); return null; }
    if (form.start_date && form.end_date && form.end_date < form.start_date) {
      setError('end_date', 'End date must be on or after start date.'); return null;
    }
    const bindTypeId   = (lockedSource?.source_type_id   ?? form.source_type_id) as number | '';
    const bindRecordId = (lockedSource?.source_record_id ?? form.source_record_id ?? form.source_obligation_id) as number | '';
    try {
      let saved: CadenceRule;
      if (isEdit && rule) {
        const payload: CadenceRuleUpdate = {
          category_id: (form.category_id as number) || undefined,
          source_type_id: (bindTypeId as number) || undefined,
          source_record_id: (bindRecordId as number) || undefined,
          accountable_role_id: (form.accountable_role_id as number) || undefined,
          fallback_role_id: (form.fallback_role_id as number) || undefined,
          start_date: form.start_date || undefined,
          end_date: form.end_date || undefined,
        };
        saved = await updateRule(rule.id, payload);
      } else {
        if (!bindTypeId || !bindRecordId) {
          setToast({ type: 'error', message: 'Source template is required.' }); return null;
        }
        const payload: CadenceRuleCreate = {
          // category_id intentionally omitted — resolved server-side.
          source_type_id: bindTypeId as number,
          source_record_id: bindRecordId as number,
          accountable_role_id: (form.accountable_role_id as number) || undefined,
          fallback_role_id: (form.fallback_role_id as number) || undefined,
          start_date: form.start_date || undefined,
          end_date: form.end_date || undefined,
        };
        saved = await createRule(payload);
      }
      setActiveRule(saved);
      return saved;
    } catch (err: any) {
      setToast({ type: 'error', message: extractApiError(err, 'Save failed.') });
      return null;
    }
  };

  // Persist exactly one stage. Returns true when navigation may proceed:
  //  - header stages (1,2) → saveHeader() succeeded
  //  - sub-sections (3-7)  → untouched (build*Payload null = no-op) OR saved OK
  //  - Versions (8)        → read-only, nothing to persist
  // Returns false only on a real validation/API failure (toast already shown).
  const saveStage = async (n: number): Promise<boolean> => {
    if (n === 1 || n === 2) {
      // Identity (1) and Authority (2) share one header row.
      return (await saveHeader()) != null;
    }
    if (!rule) return true; // defensive: sub-stages are locked until the rule exists
    try {
      switch (n) {
        case 3: {
          const p = buildScopePayload();
          if (!p) return true; // untouched → no-op
          const saved = await saveRuleScope(rule.id, p);
          setScope(saved); setScopeDiff(null);
          return true;
        }
        case 4: {
          // Periodicity holds the validity window (start/end date — header
          // columns) plus the calendar/event trigger config. Persist both.
          if (form.start_date || form.end_date) {
            const hs = await saveHeader();
            if (!hs) return false;
          }
          const p = buildTriggerPayload();
          if (p) {
            const saved = await saveRuleTrigger(rule.id, p);
            setTrigger(saved);
          }
          return true;
        }
        case 5: {
          const p = buildApplicabilityPayload();
          if (!p) return true;
          const saved = await saveRuleApplicability(rule.id, p);
          setAppConfig(saved);
          return true;
        }
        case 6: {
          // Exceptions & Parameters — both sub-sections; persist whichever the
          // user actually touched.
          const dxP = buildDateExceptionPayload();
          if (dxP) {
            const s = await saveRuleDateExceptions(rule.id, dxP);
            setDxConfig(s);
            await loadDryRun(rule.id);
          }
          const pP = buildParametersPayload();
          if (pP) {
            const s = await saveRuleParameters(rule.id, pP);
            setParamConfig(s);
          }
          return true;
        }
        default:
          return true; // 7 = Versions (read-only)
      }
    } catch (err: any) {
      setToast({ type: 'error', message: extractApiError(err, 'Save failed.') });
      return false;
    }
  };

  // ── Wizard navigation ────────────────────────────────────────────
  // Every move saves the current stage first; on failure we stay put.
  const goTo = async (target: number) => {
    if (navigating) return;
    const dest = clampStage(target, isEdit);
    if (dest === activeSection) { await saveStage(activeSection); return; }
    setNavigating(true);
    try {
      const ok = await saveStage(activeSection);
      if (ok) setActiveSection(dest);
    } finally {
      setNavigating(false);
    }
  };

  const onPrev = () => { void goTo(activeSection - 1); };
  const onNext = () => { void goTo(activeSection + 1); };

  const onBubble = (n: number) => {
    if (isBubbleLocked(n, isEdit)) return; // locked forward bubbles on a new rule
    void goTo(n);
  };

  // Last stage: save, then close + refresh the list via onSaved.
  const onFinish = async () => {
    if (navigating) return;
    setNavigating(true);
    try {
      const ok = await saveStage(activeSection);
      if (ok && rule) onSaved(rule);
    } finally {
      setNavigating(false);
    }
  };

  const handleActivate = async () => {
    const validationError = validate();
    if (validationError) { setToast({ type: 'error', message: validationError }); return; }
    if (!form.start_date) { setToast({ type: 'error', message: 'Start date is required to activate.' }); return; }
    if (!rule) return;
    // BRD 10 N#1071 — block when materialised list is empty
    const activeCount = (scope?.locations || []).filter(l => l.status === 'active').length;
    if (activeCount < 1) {
      setToast({ type: 'error', message: 'Scope must contain at least one geography. Save scope before activating.' });
      return;
    }
    setActivating(true);
    try {
      const activated = await activateRule(rule.id, form.start_date);
      onSaved(activated);
    } catch (err: any) {
      setToast({ type: 'error', message: extractApiError(err, 'Activation failed.') });
    } finally {
      setActivating(false);
    }
  };

  // Update the stable ref with the latest handler versions (defined above).
  footerCbRef.current.handleActivate = handleActivate;
  footerCbRef.current.onPrev = onPrev;
  footerCbRef.current.onNext = onNext;
  footerCbRef.current.onFinish = onFinish;

  // Push footer buttons into Modal.Footer via renderFooter prop.
  // Deps only cover values that change the footer *appearance* (disabled state, labels,
  // which buttons are visible). All callbacks are read from footerCbRef at click-time
  // so they always see the latest form/scope state without causing a dep loop.
  useEffect(() => {
    if (!footerCbRef.current.renderFooter) return;
    const onLast = isLastStage(activeSection, isEdit);
    footerCbRef.current.renderFooter(
      <div className="app-modal-footer--split" style={{ display: 'contents' }}>
        <div className="actions-left">
          {!isFirstStage(activeSection) && (
            <Button
              variant="secondary-outline"
              icon={ArrowLeft}
              data-testid="BTN_cadence_prev"
              disabled={navigating}
              onClick={() => footerCbRef.current.onPrev()}
            >
              {navigating ? 'Saving…' : 'Prev'}
            </Button>
          )}
          {canModify && isEdit && footerCbRef.current.onDelete && (
            <Button variant="delete-outline" icon={Trash2} data-testid="BTN_cadence_rule_delete" onClick={() => footerCbRef.current.onDelete?.()}>
              Delete rule
            </Button>
          )}
        </div>
        <div className="actions-right">
          {canModify && isEdit && rule?.rule_status === 'Draft' && onLast && (
            <Button
              variant="fill"
              data-testid="BTN_cadence_rule_activate"
              disabled={activating || roleConflict || navigating}
              onClick={() => footerCbRef.current.handleActivate()}
            >
              {activating ? 'Activating…' : 'Activate'}
            </Button>
          )}
          {onLast ? (
            canModify && (
              <Button
                variant={isEdit ? 'update' : 'fill'}
                icon={Check}
                data-testid="BTN_cadence_finish"
                disabled={navigating || roleConflict}
                onClick={() => footerCbRef.current.onFinish()}
              >
                {navigating ? 'Saving…' : 'Finish'}
              </Button>
            )
          ) : (
            <Button
              variant="fill"
              icon={ArrowRight}
              data-testid="BTN_cadence_next"
              disabled={navigating || roleConflict}
              onClick={() => footerCbRef.current.onNext()}
            >
              {navigating ? 'Saving…' : 'Next'}
            </Button>
          )}
        </div>
      </div>
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEdit, activeSection, navigating, activating, roleConflict, rule?.rule_status, canModify]);

  // ---- N#1071 Scope handlers ----

  const openRefreshDiff = async () => {
    if (!rule) return;
    try {
      const d = await getScopeDiff(rule.id);
      setScopeDiff(d);
      setRefreshOpen(true);
    } catch (err: any) {
      setToast({ type: 'error', message: extractApiError(err, 'Failed to load diff.') });
    }
  };

  const decideRefresh = async (decision: 'approve' | 'decline') => {
    if (!rule) return;
    try {
      await approveScopeRefresh(rule.id, decision);
      setRefreshOpen(false);
      // Reload scope after decision
      await loadScope(rule.id);
    } catch (err: any) {
      setToast({ type: 'error', message: extractApiError(err, 'Refresh failed.') });
    }
  };

  // ---- N#1072 Applicability handlers ----

  const addCondition = () => {
    const firstQ = checklistItems && checklistItems[0];
    setAppConditions(cs => [
      ...cs,
      {
        question_id: firstQ?.id ?? 0,
        operator: 'equals',
        expected_value: '',
        display_order: cs.length,
      },
    ]);
  };

  const updateCondition = (idx: number, patch: Partial<PredicateConditionInput>) => {
    setAppConditions(cs => cs.map((c, i) => (i === idx ? { ...c, ...patch } : c)));
  };

  const removeCondition = (idx: number) => {
    setAppConditions(cs => cs.filter((_, i) => i !== idx).map((c, i) => ({ ...c, display_order: i })));
  };

  const moveCondition = (idx: number, dir: -1 | 1) => {
    setAppConditions(cs => {
      const next = [...cs];
      const j = idx + dir;
      if (j < 0 || j >= next.length) return next;
      [next[idx], next[j]] = [next[j], next[idx]];
      return next.map((c, i) => ({ ...c, display_order: i }));
    });
  };

  // Mirror block — currently-selected threshold (regardless of save state)
  const selectedThreshold = thresholds.find(t => t.id === appThresholdId) ?? appConfig?.threshold ?? null;
  // Mirror block — currently-selected questionnaire (use freshly fetched questions when available)
  const selectedChecklist =
    checklists.find(c => c.id === appQuestionnaireId) ?? (appConfig?.questionnaire as any) ?? null;

  // ---- N#1073 Trigger handlers ----

  // BRD 2 T#3238 — clear frequency when span switches and the current one doesn't fit
  const SHORT_FREQS: CalendarFrequency[] = ['daily', 'weekly', 'fortnightly', 'monthly'];
  const LONG_FREQS: CalendarFrequency[] = ['bi_monthly', 'quarterly', 'half_yearly', 'annual', 'biennial'];
  const handleSpanChange = (s: TriggerSpan) => {
    setTrigSpan(s);
    if (s === 'short' && !SHORT_FREQS.includes(trigFrequency as CalendarFrequency)) {
      setTrigFrequency('');
    }
    if (s === 'long' && !LONG_FREQS.includes(trigFrequency as CalendarFrequency)) {
      setTrigFrequency('');
    }
  };


  const selectedEvent = events.find(e => e.id === trigEventId) ?? trigger?.event ?? null;

  // ---- N#1074 Date Exception handlers ----

  const toggleBusinessDay = (iso: number) => {
    setDxBusinessDays(d => {
      if (d.includes(iso)) return d.filter(x => x !== iso).sort();
      return [...d, iso].sort();
    });
  };


  const selectedCalendar = calendars.find(c => c.id === dxCalendarId) ?? dxConfig?.holiday_calendar ?? null;

  // ---- N#1075 Parameters handlers ----

  const togglePBusinessDay = (iso: number) => {
    setPBusinessDays(d => {
      if (d.includes(iso)) return d.filter(x => x !== iso).sort();
      return [...d, iso].sort();
    });
  };

  // BRD 4 — switching use_defaults → modify seeds from current globals
  const handleParamModeChange = async (m: ParametersMode) => {
    if (m === paramMode) return;
    // Constructive: use_defaults → modify
    if (m === 'modify') {
      setParamMode('modify');
      if (globalParams) {
        setPHolidayCalId(globalParams.holiday_calendar_id ?? '');
        setPBusinessDays(globalParams.business_day_definition || [1, 2, 3, 4, 5]);
        setPTimeZone(globalParams.time_zone || 'Asia/Kolkata');
        setPReminderLead(globalParams.reminder_lead_days ?? 7);
        setPGracePeriod(globalParams.grace_period_days ?? 5);
        setPCatchUpWindow(globalParams.catch_up_window_days ?? 7);
      }
      return;
    }
    // Destructive: modify → use_defaults — open warning dialog
    if (!rule) {
      // No saved rule yet — just flip the toggle, no values to discard
      setParamMode('use_defaults');
      return;
    }
    try {
      const preview = await getRuleParametersDiscardPreview(rule.id);
      setDiscardPreview(preview);
      setDiscardOpen(true);
    } catch (err: any) {
      // If preview fails (e.g. no params row yet), just flip
      setParamMode('use_defaults');
    }
  };

  const confirmDiscard = async () => {
    if (!rule) { setDiscardOpen(false); setParamMode('use_defaults'); return; }
    try {
      const saved = await saveRuleParameters(rule.id, {
        mode: 'use_defaults',
        confirm_discard: true,
      });
      setParamConfig(saved);
      setParamMode('use_defaults');
      // Reseed editable values from the now-effective (= global) values
      setPHolidayCalId(saved.holiday_calendar_id ?? '');
      setPBusinessDays(saved.business_day_definition || [1, 2, 3, 4, 5]);
      setPTimeZone(saved.time_zone || 'Asia/Kolkata');
      setPReminderLead(saved.reminder_lead_days ?? 7);
      setPGracePeriod(saved.grace_period_days ?? 5);
      setPCatchUpWindow(saved.catch_up_window_days ?? 7);
    } catch (err: any) {
      setToast({ type: 'error', message: extractApiError(err, 'Failed to discard local values.') });
    } finally {
      setDiscardOpen(false);
      setDiscardPreview(null);
    }
  };


  const globalCatchCap = globalParams?.catch_up_window_days ?? null;
  const showCatchCapHint =
    paramMode === 'modify' && globalCatchCap !== null && pCatchUpWindow > globalCatchCap;

  // (Auto-derived display values such as fulfillment_handler, template_reference,
  // regulatory_source, reference_url, policy_ref are no longer rendered in the
  // form — they live in the Dry Run modal's metadata sub-cards where the
  // operator can verify them without crowding the editor.)

  // Section-1 (Identity) is "saved" once a rule exists. Everything else is
  // gated on that — the rule_id is needed to save scope/trigger/etc.
  // Per-section status: "saved" if a DB row exists; otherwise "not saved".
  // Master save covers every section in one click, so there is no locked state.
  const identityStatus: SectionStatus = isEdit ? 'saved' : 'unsaved';
  const scopeStatus: SectionStatus = scope ? 'saved' : 'unsaved';
  const appStatus: SectionStatus = appConfig ? 'saved' : 'unsaved';
  const trigStatus: SectionStatus = trigger ? 'saved' : 'unsaved';
  const dxStatus: SectionStatus = dxConfig ? 'saved' : 'unsaved';
  const paramStatus: SectionStatus = paramConfig ? 'saved' : 'unsaved';

  // Nav items definition — drives left panel
  const navItems: { number: number; title: string; status: SectionStatus; editOnly?: boolean }[] = [
    { number: 1, title: 'Identity', status: identityStatus },
    { number: 2, title: 'Authority', status: identityStatus },
    { number: 3, title: 'Geographies', status: scopeStatus },
    { number: 4, title: 'Periodicity', status: trigStatus },
    { number: 5, title: 'Conditions', status: appStatus },
    { number: 6, title: 'Exceptions', status: dxStatus },
    { number: 7, title: 'Versions', status: 'saved', editOnly: true },
  ];

  return (
    <form className="  cadence-rule-form" onSubmit={e => e.preventDefault()}>
      <Toast toast={toast} onClose={() => setToast(null)} />

      <div className="rule-wizard">
        {/* ── Top stepper ── */}
        <CadenceStepper
          steps={navItems
            .filter(n => !n.editOnly || isEdit)
            .map<StepItem>(n => ({
              number: n.number,
              title: n.title,
              // Positional: steps before the active one are done (green); the
              // active step is rendered lavender via the `active` prop; later
              // steps stay grey. activeSection only advances after a successful
              // save, so "before active" == "successfully passed".
              status: n.number < activeSection ? 'saved' : 'unsaved',
              locked: isBubbleLocked(n.number, isEdit),
            }))}
          active={activeSection}
          onSelect={onBubble}
        />

        {/* ── Content panel ── */}
        <div className="rule-wizard-content">

          {/* ============================================================
          1. IDENTITY (rule header — category, type, source, authority, dates)
          ============================================================ */}
          {activeSection === 1 && (
            <RuleSection number={1} title="Identity" status={identityStatus}>
              {/* Category dropdown removed — the Linked-to picker below is now
                  the single source of binding. Backend still requires a
                  category_id (legacy column on SCH_cadence_rules); we ship a
                  default and let the registry kind drive semantics. */}

              <FormControl.FormGroup label="Linked to" required>
                {/* Two-step Tasks-style picker. Left = entity kind, right =
                    record of that kind. Records load when a kind is picked. */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  <FormControl.SearchSelect
                    data-testid="SEL_cadence_rule_link_type"
                    value={form.source_type_id === '' ? null : form.source_type_id}
                    onChange={(v) => {
                      const tid = v === null || v === '' ? '' : (typeof v === 'number' ? v : parseInt(String(v)));
                      const t = tid === '' ? null : (linkTypes.find(x => x.id === tid) ?? null);
                      setForm(f => ({
                        ...f,
                        source_type_id: tid as number | '',
                        source_record_id: '',
                        source_obligation_id: '',
                        source_module: '',
                        source_type: t?.type_code?.toLowerCase() ?? '',
                      }));
                    }}
                    options={linkTypes
                      .filter(t => !allowedTypeCodes || allowedTypeCodes.length === 0 || allowedTypeCodes.includes(t.type_code))
                      .map(t => ({ value: t.id, label: t.display_name }))}
                    placeholder="Policy / Risk / Control / …"
                    disabled={!!lockedSource}
                    clearable
                    noMargin
                  />
                  <FormControl.SearchSelect
                    data-testid="SEL_cadence_rule_link_record"
                    value={form.source_record_id === '' ? null : form.source_record_id}
                    onChange={(v) => {
                      const rid = v === null || v === '' ? '' : (typeof v === 'number' ? v : parseInt(String(v)));
                      setForm(f => ({
                        ...f,
                        source_record_id: rid as number | '',
                        source_obligation_id: rid as number | '',
                      }));
                    }}
                    options={linkRecordOptions}
                    placeholder={
                      !form.source_type_id
                        ? '— choose a type first —'
                        : linkRecordsLoading
                          ? 'Loading items…'
                          : linkRecordOptions.length === 0
                            ? 'No items found for this type'
                            : 'Search and pick an item'
                    }
                    disabled={!!lockedSource || !form.source_type_id || linkRecordsLoading}
                    clearable
                    noMargin
                  />
                </div>
              </FormControl.FormGroup>
            </RuleSection>
          )}

          {/* ======================================================
          SECTION B — SOURCE & AUTHORITY (N#1070)
          ====================================================== */}
          {/* ============================================================
          2. AUTHORITY (accountable + fallback) — same save endpoint as #1
          ============================================================ */}
          {activeSection === 2 && (
            <RuleSection number={2} title="Authority" status={identityStatus}>
              <FormControl.SearchSelect
                data-testid="SEL_cadence_rule_accountable_role"
                id="accountable_role_id"
                label="Accountable"
                required
                value={form.accountable_role_id === '' ? null : form.accountable_role_id}
                onChange={(value) => {
                  const id = value === null || value === '' ? '' : (typeof value === 'number' ? value : parseInt(String(value)));
                  setForm(f => ({ ...f, accountable_role_id: id as number | '' }));
                }}
                options={roles.map(r => ({ value: r.id, label: r.name }))}
                placeholder="— Select —"
                clearable
              />

              <FormControl.SearchSelect
                data-testid="SEL_cadence_rule_fallback_role"
                id="fallback_role_id"
                label="Fallback"
                value={form.fallback_role_id === '' ? null : form.fallback_role_id}
                onChange={(value) => {
                  const id = value === null || value === '' ? '' : (typeof value === 'number' ? value : parseInt(String(value)));
                  setForm(f => ({ ...f, fallback_role_id: id as number | '' }));
                }}
                options={roles.map(r => ({ value: r.id, label: r.name }))}
                placeholder="— Select —"
                clearable
                error={roleConflict ? 'Fallback must differ from accountable.' : undefined}
              />

            </RuleSection>
          )}

          {/* ======================================================
          SECTION C — SCOPE (WHO)  N#1071
          ====================================================== */}
          {/* ============================================================
          3. SCOPE — who the rule fires on
          ============================================================ */}
          {activeSection === 3 && (
            <RuleSection number={3} title="Geographies" status={scopeStatus}>

              {isEdit && scope?.needs_refresh === 1 && !refreshOpen && (
                <div className="cadence-banner banner-warning" style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <span style={{ flex: 1 }}>
                    <strong>Geography register changed.</strong>{' '}
                    The materialised list for this rule no longer matches its filter.
                  </span>
                  <Button variant="outline" data-testid="BTN_cadence_scope_review_refresh" onClick={openRefreshDiff}>
                    Review &amp; refresh
                  </Button>
                </div>
              )}

              {/* BRDs 7 & 8 — Diff modal/panel (edit only) */}
              {isEdit && refreshOpen && scopeDiff && (
                <div className="cadence-refresh-diff">
                  <h4>Proposed changes</h4>
                  <div className="diff-cols">
                    <div>
                      <strong>Additions ({scopeDiff.added.length})</strong>
                      <ul>
                        {scopeDiff.added.length === 0 && <li style={{ color: '#64748b' }}>None</li>}
                        {scopeDiff.added.map(a => (
                          <li key={`a-${a.location_id}`}>
                            {a.location_name}{' '}
                            <span style={{ color: '#64748b' }}>({a.location_type || '—'})</span>
                            {' '}— <em>{a.reason}</em>
                          </li>
                        ))}
                      </ul>
                    </div>
                    <div>
                      <strong>Removals ({scopeDiff.removed.length})</strong>
                      <ul>
                        {scopeDiff.removed.length === 0 && <li style={{ color: '#64748b' }}>None</li>}
                        {scopeDiff.removed.map(r => (
                          <li key={`r-${r.location_id}`}>
                            {r.location_name}{' '}
                            <span style={{ color: '#64748b' }}>({r.location_type || '—'})</span>
                            {' '}— <em>{r.reason}</em>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                  <div className="app-modal-footer">
                    <Button variant="outline" data-testid="BTN_cadence_scope_decline_refresh" onClick={() => decideRefresh('decline')}>Decline</Button>
                    <Button variant="fill" data-testid="BTN_cadence_scope_approve_refresh" onClick={() => decideRefresh('approve')}>Approve</Button>
                  </div>
                </div>
              )}

              <div className="cadence-mode-cards">
                <label className={'cadence-mode-card' + (scopeMode === 'specific' ? ' selected' : '')}>
                  <span className="card-head">
                    <input type="radio" name="scope_mode" data-testid="RAD_cadence_scope_mode_specific" checked={scopeMode === 'specific'}
                      onChange={() => setScopeMode('specific')} />
                    Specific (cherry-pick)
                  </span>
                </label>
                <label className={'cadence-mode-card' + (scopeMode === 'region_based' ? ' selected' : '')}>
                  <span className="card-head">
                    <input type="radio" name="scope_mode" data-testid="RAD_cadence_scope_mode_region_based" checked={scopeMode === 'region_based'}
                      onChange={() => setScopeMode('region_based')} />
                    Region-based
                  </span>
                </label>
              </div>

              {/* BRD 2 — Specific picker (multi-pick, post-migration 065) */}
              {scopeMode === 'specific' && (
                <div className="fg">
                  <label className="fg__label">
                    Geographies <span className="fg__required">*</span>
                    <span className="auto-tag">
                      cherry-pick · {specificLocationIds.length} selected
                    </span>
                  </label>
                  <p className="field-hint">
                    Specific mode is the exception path — pick the exact geographies the rule must
                    fire on. For rule-based expansion of a region's descendants, switch to Region-based.
                  </p>

                  <FormControl.MultiSelect
                    noMargin
                    placeholder="Search geographies by name or type…"
                    value={specificLocationIds}
                    onChange={vals => setSpecificLocationIds(vals.map(v => Number(v)))}
                    options={allLocations.map(l => ({
                      value: l.id,
                      label: `${l.name}${l.location_type ? ` (${l.location_type})` : ''}`,
                    }))}
                  />
                </div>
              )}

              {/* BRD 3 — Region-based sub-form */}
              {scopeMode === 'region_based' && (
                <div className="form-row">
                  <FormControl.SearchSelect
                    data-testid="SEL_cadence_scope_region_anchor"
                    id="region_anchor_id"
                    label="Region Anchor"
                    value={regionAnchorId === '' ? null : regionAnchorId}
                    onChange={(value) => {
                      const id = value === null || value === '' ? '' : (typeof value === 'number' ? value : parseInt(String(value)));
                      setRegionAnchorId(id as number | '');
                    }}
                    options={allLocations.map(l => ({
                      value: l.id,
                      label: `${l.name}${l.location_type ? ` (${l.location_type})` : ''}`,
                    }))}
                    placeholder="Global (all geographies)"
                    clearable
                  />
                  <FormControl.SearchSelect
                    data-testid="SEL_cadence_scope_type_filter"
                    id="geography_type_id"
                    label="Type Filter"
                    value={geographyTypeId === '' ? null : geographyTypeId}
                    onChange={(value) => {
                      const id = value === null || value === '' ? '' : (typeof value === 'number' ? value : parseInt(String(value)));
                      setGeographyTypeId(id as number | '');
                    }}
                    options={geographyTypes.map(t => ({ value: t.id, label: t.name }))}
                    placeholder="— No filter (all descendants) —"
                    clearable
                  />
                </div>
              )}

              {/* N#1071 — materialised resolution: the concrete child geographies
                  the current scope resolves to. Prefers the LIVE preview (computed
                  as the filter changes, before save) and falls back to the saved
                  materialised list. */}
              {(() => {
                const matLocs = (scopePreview ?? (scope?.locations || []).filter(l => l.status === 'active'));
                return (
                  <div className="cadence-materialised" data-testid="BOX_cadence_scope_materialised">
                    <div className="cadence-materialised-head">
                      <strong>Materialised geographies</strong>
                      <span className="cadence-materialised-count" data-testid="LBL_cadence_scope_materialised_count">
                        {previewLoading ? '…' : matLocs.length}
                      </span>
                    </div>
                    {matLocs.length === 0 ? (
                      <div className="cadence-picker-empty">
                        {scopeMode === 'region_based'
                          ? 'Select a region anchor and/or a type filter to see the qualifying child geographies.'
                          : 'Pick one or more geographies above.'}
                      </div>
                    ) : (
                      <div className="cadence-picker-list">
                        {matLocs.map(l => (
                          <div
                            key={l.location_id}
                            className="cadence-picker-row"
                            data-testid={`ROW_cadence_scope_materialised_${l.location_id}`}
                          >
                            <span className="cadence-picker-name">{l.location_name}</span>
                            {l.location_type && (
                              <span className="cadence-picker-type">{l.location_type}</span>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })()}


            </RuleSection>
          )}

          {/* ======================================================
          SECTION D — APPLICABILITY (WHEN)  N#1072
          ====================================================== */}
          {/* ============================================================
          4. APPLICABILITY (gate — unconditional / threshold / questionnaire)
          ============================================================ */}
          {activeSection === 5 && (
            <RuleSection number={5} title="Conditions" status={appStatus}>
              {isEdit && rule?.rule_status === 'Paused — Applicability source archived' && (
                <div className="cadence-banner banner-paused">
                  <strong>Rule paused.</strong> The referenced threshold or questionnaire was archived.
                </div>
              )}

              {(appConfig?.warnings || []).length > 0 && (
                <div className="cadence-banner banner-warning">
                  {(appConfig?.warnings || []).map((w, i) => <div key={i}>⚠ {w}</div>)}
                </div>
              )}

              {/* BRDs 1 & 2 — Three mode cards */}
              <div className="cadence-mode-cards">
                {(['unconditional', 'threshold', 'questionnaire'] as ApplicabilityMode[]).map(m => (
                  <label key={m} className={'cadence-mode-card' + (appMode === m ? ' selected' : '')}>
                    <span className="card-head">
                      <input type="radio" name="applicability_mode" data-testid={`RAD_cadence_app_mode_${m}`} checked={appMode === m}
                        onChange={() => setAppMode(m)} />
                      {m === 'unconditional' ? 'Unconditional'
                        : m === 'threshold' ? 'Threshold-driven'
                          : 'Checklist-driven'}
                    </span>
                    <span className="card-desc">
                      {m === 'unconditional' && 'Fires for every scope element on every occurrence.'}
                      {m === 'threshold' && 'Per-element gate from the Threshold Library.'}
                      {m === 'questionnaire' && 'Per-rule gate from a GRC-Common audit Checklist + content predicate.'}
                    </span>
                  </label>
                ))}
              </div>

              {/* BRD 1 T#3182 — Unconditional info note */}
              {appMode === 'unconditional' && (
                <div className="cadence-banner banner-info">
                  <strong>No filter active.</strong> The engine evaluates the due-date for every scope element on each occurrence.
                </div>
              )}

              {/* BRD 3 — Threshold-driven */}
              {appMode === 'threshold' && (
                <>
                  <FormControl.SearchSelect
                    data-testid="SEL_cadence_app_threshold"
                    id="threshold_id"
                    label="Threshold"
                    required
                    value={appThresholdId === '' ? null : appThresholdId}
                    onChange={(value) => {
                      const id = value === null || value === '' ? '' : (typeof value === 'number' ? value : parseInt(String(value)));
                      setAppThresholdId(id as number | '');
                    }}
                    options={thresholds.map(t => ({ value: t.id, label: t.name }))}
                    placeholder="— Select Threshold —"
                    clearable
                  />

                  {selectedThreshold && (
                    <div className="authority-mirror-block">
                      <h4>Threshold <span className="auto-tag">live-mirrored — read-only</span></h4>
                      <div className="fg mirror-block">
                        <label>Name</label>
                        <p className="mirror-value">{selectedThreshold.name}</p>
                      </div>
                      <div className="fg mirror-block">
                        <label>Expression</label>
                        <p className="mirror-value"><code>{selectedThreshold.expression}</code></p>
                      </div>
                      <div className="fg mirror-block">
                        <label>Evaluation Context</label>
                        <p className="mirror-value">{selectedThreshold.evaluation_context || '—'}</p>
                      </div>
                      <div className="fg mirror-block">
                        <label>Accumulation</label>
                        <p className="mirror-value">{selectedThreshold.accumulation_type === 'accumulating' ? 'Accumulating' : 'One-off'}</p>
                      </div>
                      <div className="fg mirror-block">
                        <label>Per-geography</label>
                        <p className="mirror-value">{selectedThreshold.is_geo_tagged ? 'Yes' : 'No'}</p>
                      </div>
                    </div>
                  )}
                </>
              )}

              {/* Checklist-driven — audit GRC-Common Checklist procedures (yes/no items) */}
              {appMode === 'questionnaire' && (
                <>
                  <FormControl.SearchSelect
                    data-testid="SEL_cadence_app_questionnaire"
                    id="checklist_id"
                    label="Checklist"
                    required
                    value={appQuestionnaireId === '' ? null : appQuestionnaireId}
                    onChange={(value) => {
                      const id = value === null || value === '' ? '' : (typeof value === 'number' ? value : parseInt(String(value)));
                      setAppQuestionnaireId(id as number | '');
                    }}
                    options={checklists.map(c => ({ value: c.id, label: c.name }))}
                    placeholder="— Select Checklist —"
                    clearable
                  />

                  {selectedChecklist && (
                    <div className="authority-mirror-block">
                      <h4>Checklist <span className="auto-tag">live-mirrored — read-only</span></h4>
                      <div className="fg mirror-block">
                        <label>Name</label>
                        <p className="mirror-value">{selectedChecklist.name}</p>
                      </div>
                      <div className="fg mirror-block">
                        <label>Checks ({(checklistItems || []).length})</label>
                        <ul style={{ margin: '4px 0 0 18px', fontSize: '0.85rem' }}>
                          {(checklistItems || []).map(it => (
                            <li key={it.id}>
                              <strong>Sr {it.srno}</strong> — {it.check_question}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  )}

                  {/* BRD 5 — Content predicate */}
                  <div className="fg">
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                      <label className="fg__label" style={{ margin: 0 }}>
                        Content Predicate <span className="fg__required">*</span>
                      </label>
                      <span style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>combine with</span>
                      <div style={{ width: 90 }}>
                        <FormControl.Select
                          data-testid="SEL_cadence_app_combiner"
                          value={appCombiner}
                          onChange={e => setAppCombiner(e.target.value as 'AND' | 'OR')}
                          noMargin
                        >
                          <option value="AND">AND</option>
                          <option value="OR">OR</option>
                        </FormControl.Select>
                      </div>
                    </div>

                    {appConditions.length === 0 && (
                      <p className="mirror-value" style={{ color: '#64748b' }}>No conditions yet — add at least one.</p>
                    )}

                    <div className="cadence-predicate-list">
                      {appConditions.map((c, idx) => (
                        <div key={idx} className="cadence-predicate-row">
                          <FormControl.SearchSelect
                            data-testid={`SEL_cadence_app_condition_${idx}_question`}
                            value={c.question_id === undefined || c.question_id === null ? null : c.question_id}
                            onChange={(value) => {
                              const id = value === null || value === '' ? 0 : (typeof value === 'number' ? value : parseInt(String(value)));
                              updateCondition(idx, { question_id: id });
                            }}
                            options={(checklistItems || []).map(it => ({
                              value: it.id,
                              label: `Sr ${it.srno} — ${it.check_question}`,
                            }))}
                            placeholder="— Check —"
                            clearable
                            noMargin
                          />
                          <FormControl.Select
                            data-testid={`SEL_cadence_app_condition_${idx}_operator`}
                            value={c.operator}
                            onChange={e => updateCondition(idx, { operator: e.target.value as PredicateOperator })}
                            noMargin
                          >
                            <option value="equals">equals</option>
                            <option value="not_equals">not equals</option>
                            <option value="greater_than">greater than</option>
                            <option value="less_than">less than</option>
                            <option value="contains">contains</option>
                          </FormControl.Select>
                          <FormControl.Input
                            data-testid={`INP_cadence_app_condition_${idx}_value`}
                            type="text"
                            value={c.expected_value}
                            onChange={e => updateCondition(idx, { expected_value: e.target.value })}
                            placeholder="expected value"
                            noMargin
                          />
                          <Button variant="outline" icon={ArrowUp} data-testid={`BTN_cadence_app_condition_${idx}_move_up`} onClick={() => moveCondition(idx, -1)} disabled={idx === 0} />
                          <Button variant="outline" icon={ArrowDown} data-testid={`BTN_cadence_app_condition_${idx}_move_down`} onClick={() => moveCondition(idx, 1)} disabled={idx === appConditions.length - 1} />
                          <Button variant="delete-outline" icon={X} data-testid={`BTN_cadence_app_condition_${idx}_remove`} onClick={() => removeCondition(idx)} />
                        </div>
                      ))}
                    </div>

                    <Button variant="outline" icon={Plus} data-testid="BTN_cadence_app_add_condition" onClick={addCondition}>
                      Add condition
                    </Button>
                  </div>
                </>
              )}

              {/* BRD 6 reminder */}
              <p className="hint" style={{ fontSize: '0.78rem', color: '#6b7280' }}>
                Re-evaluation timing is set globally in Engine Settings — no per-rule control here.
              </p>


            </RuleSection>
          )}

          {/* ============================================================
          5. TRIGGER — when the rule fires
          ============================================================ */}
          {activeSection === 4 && (
            <RuleSection number={4} title="Periodicity" status={trigStatus}>
              {isEdit && rule?.rule_status === 'Paused — Trigger source archived' && (
                <div className="cadence-banner banner-paused">
                  <strong>Rule paused.</strong> The referenced event was archived.
                </div>
              )}

              {(trigger?.warnings || []).length > 0 && (
                <div className="cadence-banner banner-warning">
                  {(trigger?.warnings || []).map((w, i) => <div key={i}>⚠ {w}</div>)}
                </div>
              )}

              {/* ── Validity window (the calendar window the rule fires within) ── */}
              <div className="form-row">
                <ValidatedDatePicker
                  vh={vh}
                  name="start_date"
                  code={305}
                  required={false}
                  data-testid="INP_cadence_rule_start_date"
                  id="start_date"
                  label="Start Date"
                  value={form.start_date}
                  onChange={e => setForm(f => ({ ...f, start_date: e.target.value }))}
                />
                <ValidatedDatePicker
                  vh={vh}
                  name="end_date"
                  code={305}
                  required={false}
                  data-testid="INP_cadence_rule_end_date"
                  id="end_date"
                  label="End Date"
                  value={form.end_date}
                  minDate={form.start_date ? new Date(form.start_date) : undefined}
                  onChange={e => setForm(f => ({ ...f, end_date: e.target.value }))}
                  onAfterBlur={() => {
                    if (form.start_date && form.end_date && form.end_date < form.start_date) {
                      vh.setError('end_date', 'End date must be on or after start date.');
                    }
                  }}
                />
              </div>

              {/* BRD 1 — Mode cards */}
              <div className="cadence-mode-cards">
                {(['calendar', 'event'] as TriggerMode[]).map(m => (
                  <label key={m} className={'cadence-mode-card' + (trigMode === m ? ' selected' : '')}>
                    <span className="card-head">
                      <input type="radio" name="trigger_mode" data-testid={`RAD_cadence_trig_mode_${m}`} checked={trigMode === m}
                        onChange={() => setTrigMode(m)} />
                      {m === 'calendar' ? 'Calendar-fixed' : 'Event-driven'}
                    </span>
                    <span className="card-desc">
                      {m === 'calendar'
                        ? 'Periodic firing on a wall-clock schedule (Daily, Weekly, Quarterly, …).'
                        : 'Fire after an anchor event occurs — or when it has not occurred by a deadline.'}
                    </span>
                  </label>
                ))}
              </div>

              {/* ===== Mode A — Calendar-fixed ===== */}
              {trigMode === 'calendar' && (
                <>
                  {/* BRD 2 — Span */}
                  <div className="cadence-subcards">
                    {(['short', 'long'] as TriggerSpan[]).map(s => (
                      <label key={s} className={'cadence-subcard' + (trigSpan === s ? ' selected' : '')}>
                        <input type="radio" name="trig_span" data-testid={`RAD_cadence_trig_span_${s}`} checked={trigSpan === s}
                          onChange={() => handleSpanChange(s)} />
                        <span className="subcard-body">
                          <span className="subcard-title">{s === 'short' ? 'Short span' : 'Long span'}</span>
                          <span className="subcard-desc">
                            {s === 'short' ? '≤ Monthly' : '> Monthly (with month-of-period drill-down)'}
                          </span>
                        </span>
                      </label>
                    ))}
                  </div>

                  {/* BRD 3/4 — Frequency */}
                  <FormControl.SearchSelect
                    data-testid="SEL_cadence_trig_frequency"
                    id="trig_frequency"
                    label="Frequency"
                    required
                    value={trigFrequency === '' ? null : trigFrequency}
                    onChange={(value) => setTrigFrequency((value === null ? '' : String(value)) as CalendarFrequency | '')}
                    options={(trigSpan === 'short' ? SHORT_FREQS : LONG_FREQS).map(f => ({
                      value: f,
                      label: f.replace('_', '-'),
                    }))}
                    placeholder="— Select Frequency —"
                    clearable
                  />

                  {/* BRD 3 — Weekly/Fortnightly day-of-week */}
                  {(trigFrequency === 'weekly' || trigFrequency === 'fortnightly') && (
                    <FormControl.SearchSelect
                      data-testid="SEL_cadence_trig_dow"
                      id="trig_dow"
                      label="Day of week"
                      required
                      value={trigDayOfWeek === '' ? null : trigDayOfWeek}
                      onChange={(value) => setTrigDayOfWeek((value === null ? '' : String(value)) as DayOfWeek | '')}
                      options={['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'].map(d => ({
                        value: d,
                        label: d,
                      }))}
                      placeholder="— Day —"
                      clearable
                    />
                  )}

                  {/* BRD 4 — Long span: which month of the period */}
                  {trigSpan === 'long' && trigFrequency && (
                    <FormControl.SearchSelect
                      data-testid="SEL_cadence_trig_mop"
                      id="trig_mop"
                      label="Which month within the period"
                      required
                      value={trigMonthOfPeriod === '' ? null : trigMonthOfPeriod}
                      onChange={(value) => {
                        const n = value === null || value === '' ? '' : (typeof value === 'number' ? value : parseInt(String(value)));
                        setTrigMonthOfPeriod(n as number | '');
                      }}
                      options={(() => {
                        if (trigFrequency === 'bi_monthly') return [1, 2].map(n => ({ value: n, label: String(n) }));
                        if (trigFrequency === 'quarterly') return [1, 2, 3].map(n => ({ value: n, label: String(n) }));
                        if (trigFrequency === 'half_yearly') return [1, 2, 3, 4, 5, 6].map(n => ({ value: n, label: String(n) }));
                        if (trigFrequency === 'annual' || trigFrequency === 'biennial') {
                          return ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December']
                            .map((m, i) => ({ value: i + 1, label: m }));
                        }
                        return [];
                      })()}
                      placeholder="— Month —"
                      clearable
                    />
                  )}

                  {/* BRD 3/4 — Monthly specifier */}
                  {(trigFrequency === 'monthly' || trigSpan === 'long') && (
                    <>
                      <FormControl.SearchSelect
                        data-testid="SEL_cadence_trig_monthly_spec"
                        id="trig_spec"
                        label="Day within the month"
                        required
                        value={trigMonthlySpec === '' ? null : trigMonthlySpec}
                        onChange={(value) => setTrigMonthlySpec((value === null ? '' : String(value)) as MonthlySpecifier | '')}
                        options={[
                          { value: 'first_day', label: 'First day of month' },
                          { value: 'last_day', label: 'Last day of month' },
                          { value: 'nth_weekday', label: 'Nth weekday' },
                          { value: 'specified_day', label: 'Specified day' },
                        ]}
                        placeholder="— Specifier —"
                        clearable
                      />

                      {trigMonthlySpec === 'specified_day' && (
                        <FormControl.Input
                          data-testid="INP_cadence_trig_specified_day"
                          id="trig_day"
                          label="Day (1–31)"
                          type="number"
                          min={1}
                          max={31}
                          value={String(trigSpecifiedDay)}
                          onChange={e => setTrigSpecifiedDay(e.target.value ? parseInt(e.target.value) : '')}
                          helper="Day-overflow rule applies (e.g. 31 → last day of February)."
                        />
                      )}

                      {trigMonthlySpec === 'nth_weekday' && (
                        <div className="form-row">
                          <FormControl.SearchSelect
                            data-testid="SEL_cadence_trig_nth_n"
                            id="trig_nth_n"
                            label="Nth"
                            value={trigNthN === '' ? null : trigNthN}
                            onChange={(value) => {
                              const n = value === null || value === '' ? '' : (typeof value === 'number' ? value : parseInt(String(value)));
                              setTrigNthN(n as number | '');
                            }}
                            options={[
                              ...[1, 2, 3, 4, 5].map(n => ({
                                value: n,
                                label: `${n}${['st', 'nd', 'rd', 'th', 'th'][n - 1]}`,
                              })),
                              { value: -1, label: 'Last' },
                            ]}
                            placeholder="— N —"
                            clearable
                          />
                          <FormControl.SearchSelect
                            data-testid="SEL_cadence_trig_nth_day"
                            id="trig_nth_day"
                            label="Weekday"
                            value={trigNthDay === '' ? null : trigNthDay}
                            onChange={(value) => setTrigNthDay((value === null ? '' : String(value)) as DayOfWeek | '')}
                            options={['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'].map(d => ({
                              value: d,
                              label: d,
                            }))}
                            placeholder="— Weekday —"
                            clearable
                          />
                        </div>
                      )}
                    </>
                  )}

                  {/* BRD 5 — Period-token format hint (read-only) */}
                  {trigFrequency && (
                    <p className="hint" style={{ fontSize: '0.78rem', color: '#6b7280' }}>
                      Period token format (used in event_key dedup): <code>
                        {trigger?.period_token_format
                          ?? ({
                            daily: 'YYYY-MM-DD', weekly: 'YYYY-Wnn', fortnightly: 'YYYY-Fnn',
                            monthly: 'YYYY-MM', bi_monthly: 'YYYY-BMn', quarterly: 'YYYY-Qn',
                            half_yearly: 'YYYY-Hn', annual: 'YYYY', biennial: 'YYYY-B',
                          } as Record<string, string>)[trigFrequency]
                          ?? 'YYYY-MM-DD'}
                      </code>
                    </p>
                  )}
                </>
              )}

              {/* ===== Mode B — Event-driven ===== */}
              {trigMode === 'event' && (
                <>
                  <FormControl.SearchSelect
                    data-testid="SEL_cadence_trig_event"
                    id="trig_event"
                    label="Event"
                    required
                    value={trigEventId === '' ? null : trigEventId}
                    onChange={(value) => {
                      const id = value === null || value === '' ? '' : (typeof value === 'number' ? value : parseInt(String(value)));
                      setTrigEventId(id as number | '');
                    }}
                    options={events.map(ev => ({ value: ev.id, label: `${ev.code} — ${ev.name}` }))}
                    placeholder="— Select Event —"
                    clearable
                  />

                  {/* BRD 6 — Event mirror block */}
                  {selectedEvent && (
                    <div className="authority-mirror-block">
                      <h4>Event <span className="auto-tag">live-mirrored — read-only</span></h4>
                      <div className="fg mirror-block">
                        <label>Name</label>
                        <p className="mirror-value">{selectedEvent.name}</p>
                      </div>
                      <div className="fg mirror-block">
                        <label>Type</label>
                        <p className="mirror-value">{selectedEvent.event_type || '—'}</p>
                      </div>
                      <div className="fg mirror-block">
                        <label>Source Module</label>
                        <p className="mirror-value">{selectedEvent.source_module || '—'}</p>
                      </div>
                      <div className="fg mirror-block">
                        <label>Schema</label>
                        <p className="mirror-value"><code>{JSON.stringify(selectedEvent.event_schema || {})}</code></p>
                      </div>
                      <div className="fg mirror-block">
                        <label>Measured periodically</label>
                        <p className="mirror-value">{selectedEvent.measure_periodically ? 'Yes' : 'No'}</p>
                      </div>
                      <div className="fg mirror-block">
                        <label>Geo-tagged</label>
                        <p className="mirror-value">{selectedEvent.is_geo_tagged ? 'Yes' : 'No'}</p>
                      </div>
                    </div>
                  )}

                  {/* BRD 7 — Variant cards */}
                  <div className="cadence-subcards">
                    {(['after_event', 'watch_not_happened'] as TriggerVariant[]).map(v => (
                      <label key={v} className={'cadence-subcard' + (trigVariant === v ? ' selected' : '')}>
                        <input type="radio" name="trig_variant" data-testid={`RAD_cadence_trig_variant_${v}`} checked={trigVariant === v}
                          onChange={() => setTrigVariant(v)} />
                        <span className="subcard-body">
                          <span className="subcard-title">{v === 'after_event' ? 'After-event' : 'Watch-not-happened'}</span>
                          <span className="subcard-desc">
                            {v === 'after_event'
                              ? 'Fire offset days after anchor event.'
                              : 'Fire when anchor event has NOT occurred by a deadline.'}
                          </span>
                        </span>
                      </label>
                    ))}
                  </div>

                  {/* BRD 8 — After-event */}
                  {trigVariant === 'after_event' && (
                    <>
                      <div className="form-row">
                        <FormControl.Input
                          data-testid="INP_cadence_trig_offset_value"
                          id="trig_off_v"
                          label="Offset"
                          type="number"
                          min={0}
                          value={String(trigOffsetValue)}
                          onChange={e => setTrigOffsetValue(e.target.value ? parseInt(e.target.value) : '')}
                        />
                        <FormControl.SearchSelect
                          data-testid="SEL_cadence_trig_offset_unit"
                          id="trig_off_u"
                          label="Unit"
                          value={trigOffsetUnit}
                          onChange={(value) => setTrigOffsetUnit((value === null ? 'days' : String(value)) as OffsetUnit)}
                          options={[
                            { value: 'days', label: 'Days' },
                            { value: 'weeks', label: 'Weeks' },
                            { value: 'months', label: 'Months' },
                            { value: 'years', label: 'Years' },
                          ]}
                          placeholder="Unit"
                        />
                        <FormControl.SearchSelect
                          data-testid="SEL_cadence_trig_anchor_from"
                          id="trig_anchor"
                          label="Anchor from"
                          value={trigAnchorFrom}
                          onChange={(value) => setTrigAnchorFrom((value === null ? 'completion_date' : String(value)) as AnchorFrom)}
                          options={[
                            { value: 'completion_date', label: 'Completion date' },
                            { value: 'due_date', label: 'Due date' },
                          ]}
                          placeholder="Anchor from"
                        />
                      </div>

                      <p className="hint" style={{ fontSize: '0.78rem', color: '#6b7280' }}>
                        Offsets are always forward. Use the parameters section for lead-time reminders, not here.
                      </p>

                      {/* BRD 9 — Watch window grace */}
                      <FormControl.Input
                        data-testid="INP_cadence_trig_grace"
                        id="trig_grace"
                        label="Watch-window grace (days)"
                        type="number"
                        min={0}
                        value={String(trigGrace)}
                        onChange={e => setTrigGrace(e.target.value ? parseInt(e.target.value) : 0)}
                        helper="Anchor event still counts if it occurs within (period_end + grace)."
                      />

                      {/* BRD 10 — First-fire seeding */}
                      <div className="fg">
                        <label className="fg__label">First-fire seeding</label>
                        <div className="cadence-subcards">
                          {(['wait_first_anchor', 'seed_date'] as FirstFireMode[]).map(ff => (
                            <label key={ff} className={'cadence-subcard' + (trigFirstFire === ff ? ' selected' : '')}>
                              <input type="radio" name="trig_ff" data-testid={`RAD_cadence_trig_first_fire_${ff}`} checked={trigFirstFire === ff}
                                onChange={() => setTrigFirstFire(ff)} />
                              <span className="subcard-body">
                                <span className="subcard-title">
                                  {ff === 'wait_first_anchor' ? 'Wait for first anchor' : 'Fire once on seed date'}
                                </span>
                              </span>
                            </label>
                          ))}
                        </div>
                        {trigFirstFire === 'seed_date' && (
                          <ValidatedDatePicker
                            vh={vh}
                            name="trig_seed_date"
                            code={305}
                            required={false}
                            data-testid="INP_cadence_trig_seed_date"
                            value={trigSeedDate}
                            onChange={e => setTrigSeedDate(e.target.value)}
                          />
                        )}
                      </div>
                    </>
                  )}

                  {/* BRD 11 — Watch-not-happened */}
                  {trigVariant === 'watch_not_happened' && (
                    <>
                      <div className="fg">
                        <label className="fg__label">Deadline kind</label>
                        <div className="cadence-subcards">
                          {(['absolute', 'offset'] as DeadlineKind[]).map(dk => (
                            <label key={dk} className={'cadence-subcard' + (trigDeadlineKind === dk ? ' selected' : '')}>
                              <input type="radio" name="trig_dk" data-testid={`RAD_cadence_trig_deadline_kind_${dk}`} checked={trigDeadlineKind === dk}
                                onChange={() => setTrigDeadlineKind(dk)} />
                              <span className="subcard-body">
                                <span className="subcard-title">
                                  {dk === 'absolute' ? 'Absolute date' : 'Offset from reference'}
                                </span>
                              </span>
                            </label>
                          ))}
                        </div>
                      </div>
                      {trigDeadlineKind === 'absolute' && (
                        <ValidatedDatePicker
                          vh={vh}
                          name="trig_deadline_date"
                          code={305}
                          required={false}
                          data-testid="INP_cadence_trig_deadline_date"
                          id="trig_dl_date"
                          label="Deadline date"
                          value={trigDeadlineDate}
                          onChange={e => setTrigDeadlineDate(e.target.value)}
                        />
                      )}
                      {trigDeadlineKind === 'offset' && (
                        <div className="form-row">
                          <FormControl.Input
                            data-testid="INP_cadence_trig_deadline_offset"
                            id="trig_dl_off"
                            label="Offset (days)"
                            type="number"
                            min={0}
                            value={String(trigDeadlineOffset)}
                            onChange={e => setTrigDeadlineOffset(e.target.value ? parseInt(e.target.value) : '')}
                          />
                          <FormControl.SearchSelect
                            data-testid="SEL_cadence_trig_deadline_ref"
                            id="trig_dl_ref"
                            label="From"
                            value={trigDeadlineRef}
                            onChange={(value) => setTrigDeadlineRef((value === null ? 'period_start' : String(value)) as DeadlineReference)}
                            options={[
                              { value: 'period_start', label: 'Period start' },
                              { value: 'period_end', label: 'Period end' },
                            ]}
                            placeholder="From"
                          />
                        </div>
                      )}
                    </>
                  )}
                </>
              )}


            </RuleSection>
          )}

          {/* ============================================================
          6. DATE EXCEPTIONS — shift behaviour when due falls on a non-working day
          ============================================================ */}
          {activeSection === 6 && (
            <RuleSection number={6} title="Exceptions" status={dxStatus}
              hint="Non-working-day handling and scheduling parameters.">

              {/* ── Date exceptions ── */}
              <h4 className="rule-subsection-title">Date exceptions</h4>

              {/* BRD 2 — Action cards */}
              <div className="fg">
                <label className="fg__label">If due date falls on a non-working day <span className="fg__required">*</span></label>
                <div className="cadence-mode-cards">
                  {([
                    { v: 'strict', title: 'Strict', desc: 'Fire as-is on the computed date.' },
                    { v: 'shift_forward', title: 'Shift forward', desc: 'Fire on the next working day.' },
                    { v: 'shift_backward', title: 'Shift backward', desc: 'Fire on the previous working day.' },
                    { v: 'shift_to_nearest', title: 'Shift to nearest', desc: 'Pick whichever working day is closer.' },
                  ] as { v: DateExceptionAction; title: string; desc: string }[]).map(a => (
                    <label key={a.v} className={'cadence-mode-card' + (dxAction === a.v ? ' selected' : '')}>
                      <span className="card-head">
                        <input type="radio" name="dx_action" data-testid={`RAD_cadence_dx_action_${a.v}`} checked={dxAction === a.v}
                          onChange={() => setDxAction(a.v)} />
                        {a.title}
                      </span>
                      <span className="card-desc">{a.desc}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* BRD 3 — Business-day picker */}
              <div className="fg">
                <label className="fg__label">Business days <span className="fg__required">*</span></label>
                <div className="cadence-chip-row">
                  {([
                    { iso: 1, label: 'Mon' }, { iso: 2, label: 'Tue' }, { iso: 3, label: 'Wed' },
                    { iso: 4, label: 'Thu' }, { iso: 5, label: 'Fri' }, { iso: 6, label: 'Sat' },
                    { iso: 7, label: 'Sun' },
                  ]).map(d => {
                    const on = dxBusinessDays.includes(d.iso);
                    return (
                      <label key={d.iso} className={'cadence-chip' + (on ? ' selected' : '')}>
                        <input type="checkbox" data-testid={`CHK_cadence_dx_biz_day_${d.iso}`} checked={on} onChange={() => toggleBusinessDay(d.iso)} />
                        {d.label}
                      </label>
                    );
                  })}
                </div>
              </div>

              {/* BRD 3 — Holiday calendar */}
              <FormControl.SearchSelect
                data-testid="SEL_cadence_dx_calendar"
                id="dx_calendar"
                label="Holiday calendar"
                value={dxCalendarId === '' ? null : dxCalendarId}
                onChange={(value) => {
                  const id = value === null || value === '' ? '' : (typeof value === 'number' ? value : parseInt(String(value)));
                  setDxCalendarId(id as number | '');
                }}
                options={calendars.map(c => ({ value: c.id, label: `${c.code} — ${c.name}` }))}
                placeholder="— None (only weekends count as non-working) —"
                clearable
                helper={selectedCalendar && selectedCalendar.holidays && selectedCalendar.holidays.length > 0
                  ? `${selectedCalendar.holidays.length} date${selectedCalendar.holidays.length === 1 ? '' : 's'} configured.`
                  : undefined}
              />


              {/* ── Parameters ── */}
              <h4 className="rule-subsection-title">Parameters</h4>

              {/* BRD 1 — Two-mode toggle */}
              <div className="cadence-mode-cards">
                {(['use_defaults', 'modify'] as ParametersMode[]).map(m => (
                  <label key={m} className={'cadence-mode-card' + (paramMode === m ? ' selected' : '')}>
                    <span className="card-head">
                      <input type="radio" name="param_mode" data-testid={`RAD_cadence_param_mode_${m}`} checked={paramMode === m}
                        onChange={() => handleParamModeChange(m)} />
                      {m === 'use_defaults' ? 'Use defaults' : 'Modify'}
                    </span>
                    <span className="card-desc">
                      {m === 'use_defaults'
                        ? 'Inherit current tenant globals at runtime — no rule-level copy.'
                        : 'Materialise values onto this rule — engine reads rule-level only.'}
                    </span>
                  </label>
                ))}
              </div>

              {/* BRD 6 — Sub-group: Calendar & clock */}
              <div className="cadence-param-subgroup">
                <h5>Calendar &amp; clock</h5>
                <FormControl.SearchSelect
                  data-testid="SEL_cadence_param_holiday_cal"
                  id="p_cal"
                  label="Holiday calendar"
                  value={pHolidayCalId === '' ? null : pHolidayCalId}
                  onChange={(value) => {
                    const id = value === null || value === '' ? '' : (typeof value === 'number' ? value : parseInt(String(value)));
                    setPHolidayCalId(id as number | '');
                  }}
                  options={calendars.map(c => ({ value: c.id, label: `${c.code} — ${c.name}` }))}
                  placeholder="— None —"
                  clearable
                  disabled={paramMode === 'use_defaults'}
                />

                <div className="fg">
                  <label className="fg__label">Business-day definition</label>
                  <div className="cadence-chip-row">
                    {([
                      { iso: 1, label: 'Mon' }, { iso: 2, label: 'Tue' }, { iso: 3, label: 'Wed' },
                      { iso: 4, label: 'Thu' }, { iso: 5, label: 'Fri' }, { iso: 6, label: 'Sat' },
                      { iso: 7, label: 'Sun' },
                    ]).map(d => {
                      const on = pBusinessDays.includes(d.iso);
                      return (
                        <label key={d.iso}
                          className={'cadence-chip' + (on ? ' selected' : '')}
                          style={paramMode === 'use_defaults' ? { pointerEvents: 'none', opacity: 0.7 } : undefined}>
                          <input type="checkbox" data-testid={`CHK_cadence_param_biz_day_${d.iso}`} checked={on}
                            onChange={() => togglePBusinessDay(d.iso)}
                            disabled={paramMode === 'use_defaults'} />
                          {d.label}
                        </label>
                      );
                    })}
                  </div>
                </div>

                <FormControl.Input
                  data-testid="INP_cadence_param_timezone"
                  id="p_tz"
                  label="Time zone"
                  type="text"
                  value={pTimeZone}
                  onChange={e => setPTimeZone(e.target.value)}
                  disabled={paramMode === 'use_defaults'}
                  placeholder="IANA name (e.g. Asia/Kolkata)"
                />
              </div>

              {/* BRD 6 — Sub-group: Tolerances */}
              <div className="cadence-param-subgroup">
                <h5>Tolerances</h5>
                <div className="form-row">
                  <FormControl.Input
                    data-testid="INP_cadence_param_reminder_lead"
                    id="p_rl"
                    label="Reminder lead (days before due)"
                    type="number"
                    min={0}
                    value={String(pReminderLead)}
                    onChange={e => setPReminderLead(parseInt(e.target.value || '0'))}
                    disabled={paramMode === 'use_defaults'}
                  />
                  <FormControl.Input
                    data-testid="INP_cadence_param_grace"
                    id="p_grace"
                    label="Grace period (days after due)"
                    type="number"
                    min={0}
                    value={String(pGracePeriod)}
                    onChange={e => setPGracePeriod(parseInt(e.target.value || '0'))}
                    disabled={paramMode === 'use_defaults'}
                  />
                  <FormControl.Input
                    data-testid="INP_cadence_param_catchup"
                    id="p_catch"
                    label="Catch-up window (days after engine downtime)"
                    type="number"
                    min={0}
                    value={String(pCatchUpWindow)}
                    onChange={e => setPCatchUpWindow(parseInt(e.target.value || '0'))}
                    disabled={paramMode === 'use_defaults'}
                    helper={showCatchCapHint
                      ? `Capped at global engine catch-up window (${globalCatchCap} days). Effective value at runtime will be ${globalCatchCap}.`
                      : undefined}
                  />
                </div>
              </div>

              {/* BRD 8 reminder — global propagation note */}
              {paramMode === 'use_defaults' && globalParams && (
                <div className="cadence-banner banner-info" style={{ fontSize: '0.82rem' }}>
                  <strong>Currently inheriting:</strong>{' '}
                  timezone <code>{globalParams.time_zone}</code>,
                  reminder lead <code>{globalParams.reminder_lead_days}d</code>,
                  grace <code>{globalParams.grace_period_days}d</code>,
                  catch-up <code>{globalParams.catch_up_window_days}d</code>.
                  Tenant admin changes propagate live.
                </div>
              )}


            </RuleSection>
          )}

          {/* BRD 5 — destructive-switch dialog */}
          {discardOpen && (
            <div className="cadence-dialog-backdrop" onClick={() => setDiscardOpen(false)}>
              <div className="cadence-dialog" onClick={e => e.stopPropagation()}>
                <h3>Discard local parameter values?</h3>
                <div className="danger-text">
                  <strong>This action is irreversible.</strong>{' '}
                  {discardPreview?.warning
                    || 'Local parameter values will be permanently discarded and cannot be auto-restored. A future switch back to Modify will start from the then-current global values.'}
                </div>
                {discardPreview?.local_values && (
                  <>
                    <div className="values-list">
                      <div className="row"><span className="k">Holiday calendar</span><span className="v">{String(discardPreview.local_values.holiday_calendar_id ?? '—')}</span></div>
                      <div className="row"><span className="k">Business-day definition</span><span className="v">{JSON.stringify(discardPreview.local_values.business_day_definition ?? [])}</span></div>
                      <div className="row"><span className="k">Time zone</span><span className="v">{discardPreview.local_values.time_zone || '—'}</span></div>
                      <div className="row"><span className="k">Reminder lead (days)</span><span className="v">{String(discardPreview.local_values.reminder_lead_days ?? '—')}</span></div>
                      <div className="row"><span className="k">Grace period (days)</span><span className="v">{String(discardPreview.local_values.grace_period_days ?? '—')}</span></div>
                      <div className="row"><span className="k">Catch-up window (days)</span><span className="v">{String(discardPreview.local_values.catch_up_window_days ?? '—')}</span></div>
                    </div>
                  </>
                )}
                <div className="dialog-actions">
                  <Button variant="outline" data-testid="BTN_cadence_discard_cancel" onClick={() => { setDiscardOpen(false); setDiscardPreview(null); }}>
                    Cancel
                  </Button>
                  <Button variant="delete-outline" data-testid="BTN_cadence_discard_confirm" onClick={confirmDiscard}>
                    Discard &amp; switch to Use defaults
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* ============================================================
          8. VERSIONS (edit-only)
          ============================================================ */}
          {isEdit && activeSection === 7 && (
            <RuleSection number={7} title="Versions" status="saved">
              {versionsLoading ? (
                <p className="rule-section-hint">Loading…</p>
              ) : versions ? (
                <>
                  <div>
                    <strong>Current version: v{versions.current_version_number}</strong>
                    {versions.current_active_from && (
                      <> · active from <code>{versions.current_active_from}</code></>
                    )}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'flex-end', gap: 12 }}>
                    <div style={{ flex: 1 }}>
                      <FormControl.Input
                        data-testid="INP_cadence_version_rollout_notes"
                        id="rollout_notes"
                        label="Rollout notes"
                        type="text"
                        placeholder="e.g. Q2 audit scope rollout"
                        value={rolloutNotes}
                        onChange={e => setRolloutNotes(e.target.value)}
                        maxLength={2000}
                        error={errors['rollout_notes']}
                        onBlur={(e) => {
                          if (e.target.value.trim()) validateField('rollout_notes', 110, e.target.value);
                          else clearError('rollout_notes');
                        }}
                      />
                    </div>
                    <div style={{ flexShrink: 0, paddingBottom: 2 }}>
                      <Button variant="fill" data-testid="BTN_cadence_version_rollout" onClick={handleRollout} disabled={rolloutBusy}>
                        {rolloutBusy ? 'Rolling out…' : `Roll out v${versions.current_version_number + 1}`}
                      </Button>
                    </div>
                  </div>
                  {versions.history.length > 0 && (
                    <div className="fg">
                      <label className="fg__label">History ({versions.history.length})</label>
                      <ul style={{ margin: '4px 0 0 18px', fontSize: '0.85rem' }}>
                        {versions.history.map(v => (
                          <li key={v.id} style={{ marginBottom: 4 }}>
                            <strong>v{v.version_number}</strong>{' '}
                            <span style={{ color: '#64748b' }}>
                              <code>{v.active_from}</code> → <code>{v.active_until}</code>
                              {' · '}{v.scope_snapshot?.length ?? 0} geo
                              {v.notes ? <> · "{v.notes}"</> : null}
                            </span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </>
              ) : null}
            </RuleSection>
          )}

        </div>  {/* close rule-wizard-content */}
      </div>    {/* close rule-wizard */}

    </form>
  );
};

export default CadenceRuleForm;
